import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';
import {
  createBoothOption,
  fetchEventBoothMap,
  releaseBoothOption,
  releaseBoothReservation,
} from '../../../../utils/boothBookingApi';
import {
  copyBoothAsset,
  uploadBoothAsset,
  getBoothAssetUrl,
  removeBoothAsset,
  readBoothAssetText,
  readSvgDimensions,
  readImageDimensions,
} from '../../../../utils/boothAssets';
import {
  BOOTH_PRODUCT_TYPES,
  areasToExportJson,
  boothLockId,
  compareBoothNumbers,
  contentTypeForFileName,
  isValidVariantCode,
  isVariantProduct,
  normalizeBoothNumber,
  normalizeVariant,
  parseVariantAssignmentList,
  polygonCentroid,
  validateBoothMatchRule,
} from '../../../../utils/boothMap';
import {
  retargetMultiLang,
  retargetTemplateNames,
  scaleTemplateComponents,
  templateRetargetValues,
} from '../../../../utils/boothOfferAdmin';
import {
  parseHallPlan,
  matchWithSystem,
  standsToAreaInputs,
  sortIssues,
  issueCounts,
  viewBoxToString,
  parseViewBoxString,
  planFromAreas,
  systemStandsFromMap,
  isUnnamedBoothNumber,
  activationBlockers,
  canActivatePlan,
  unnamedCount,
} from '../../../../utils/hallplanParser';

/**
 * Data + actions of the admin event tab "Hall plan & booths" (HallPlanTab.jsx):
 * Event.boothPlan (plan image + coordinate space), the BoothPlanArea rows of the event,
 * the hall plan versions (SVG import via utils/hallplanParser; Event.boothPlan.activeVersionId
 * selects the live area set, null = legacy rows without planVersionId),
 * the booth map (getEventBoothMap, admin call) and the booth EventProducts.
 *
 * Loaders catch their own errors (state `errors`). Actions throw - the tab wraps them,
 * reports to Sentry and shows the message.
 */

const client = generateClient();
const FEATURE = 'booth-admin';
const PAGE_LIMIT = 1000;
const CHUNK_SIZE = 10;

const BOOTH_PLAN_FIELDS = /* GraphQL */ `
  boothPlan {
    width
    height
    areasUpdatedAt
    viewBox
    activeVersionId
    image {
      fileName
      s3Path
      type
    }
  }
`;

const GET_EVENT_BOOTH_PLAN = /* GraphQL */ `
  query GetEventBoothPlan($id: ID!) {
    getEvent(id: $id) {
      id
      name {
        language
        text
      }
      ${BOOTH_PLAN_FIELDS}
    }
  }
`;

const UPDATE_EVENT_BOOTH_PLAN = /* GraphQL */ `
  mutation UpdateEventBoothPlan($input: UpdateEventInput!) {
    updateEvent(input: $input) {
      id
      ${BOOTH_PLAN_FIELDS}
    }
  }
`;

const AREA_FIELDS = /* GraphQL */ `
  id
  eventId
  boothNumber
  points
  labelX
  labelY
  source
  planVersionId
  labelSize
  areaUnits
  bbox
  variant
  size
  bookable
  positionType
  zone
  openSides
  attributes
  updatedAt
`;

const BOOTH_PLAN_AREAS_BY_EVENT = /* GraphQL */ `
  query BoothPlanAreasByEvent($eventId: ID!, $limit: Int, $nextToken: String) {
    boothPlanAreasByEvent(eventId: $eventId, limit: $limit, nextToken: $nextToken) {
      items {
        ${AREA_FIELDS}
      }
      nextToken
    }
  }
`;

const CREATE_BOOTH_PLAN_AREA = /* GraphQL */ `
  mutation CreateBoothPlanArea($input: CreateBoothPlanAreaInput!) {
    createBoothPlanArea(input: $input) {
      ${AREA_FIELDS}
    }
  }
`;

const UPDATE_BOOTH_PLAN_AREA = /* GraphQL */ `
  mutation UpdateBoothPlanArea($input: UpdateBoothPlanAreaInput!) {
    updateBoothPlanArea(input: $input) {
      ${AREA_FIELDS}
    }
  }
`;

const DELETE_BOOTH_PLAN_AREA = /* GraphQL */ `
  mutation DeleteBoothPlanArea($input: DeleteBoothPlanAreaInput!) {
    deleteBoothPlanArea(input: $input) {
      id
    }
  }
`;

const BOOTH_PLAN_AREAS_BY_PLAN_VERSION = /* GraphQL */ `
  query BoothPlanAreasByPlanVersion($planVersionId: ID!, $limit: Int, $nextToken: String) {
    boothPlanAreasByPlanVersion(planVersionId: $planVersionId, limit: $limit, nextToken: $nextToken) {
      items {
        ${AREA_FIELDS}
      }
      nextToken
    }
  }
`;

const HALL_PLAN_VERSION_FIELDS = /* GraphQL */ `
  id
  eventId
  status
  fileName
  viewBox
  width
  height
  sourceFile {
    fileName
    s3Path
    type
  }
  backgroundFile {
    fileName
    s3Path
    type
  }
  summary
  issues
  matchSummary
  standsCount
  errorCount
  warningCount
  unitsPerMeter
  uploadedBy
  activatedAt
  notes
  createdAt
  updatedAt
`;

const HALL_PLAN_VERSIONS_BY_EVENT = /* GraphQL */ `
  query HallPlanVersionsByEvent($eventId: ID!, $limit: Int, $nextToken: String) {
    hallPlanVersionsByEvent(eventId: $eventId, limit: $limit, nextToken: $nextToken) {
      items {
        ${HALL_PLAN_VERSION_FIELDS}
      }
      nextToken
    }
  }
`;

const CREATE_HALL_PLAN_VERSION = /* GraphQL */ `
  mutation CreateHallPlanVersion($input: CreateHallPlanVersionInput!) {
    createHallPlanVersion(input: $input) {
      ${HALL_PLAN_VERSION_FIELDS}
    }
  }
`;

const UPDATE_HALL_PLAN_VERSION = /* GraphQL */ `
  mutation UpdateHallPlanVersion($input: UpdateHallPlanVersionInput!) {
    updateHallPlanVersion(input: $input) {
      ${HALL_PLAN_VERSION_FIELDS}
    }
  }
`;

const DELETE_HALL_PLAN_VERSION = /* GraphQL */ `
  mutation DeleteHallPlanVersion($input: DeleteHallPlanVersionInput!) {
    deleteHallPlanVersion(input: $input) {
      id
    }
  }
`;

const CREATE_EVENT_PRODUCT = /* GraphQL */ `
  mutation CreateEventProduct($input: CreateEventProductInput!) {
    createEventProduct(input: $input) {
      id
      price
      stock
      isAvailable
      sortKey
      createdAt
    }
  }
`;

const UPDATE_EVENT_PRODUCT = /* GraphQL */ `
  mutation UpdateEventProduct($input: UpdateEventProductInput!) {
    updateEventProduct(input: $input) {
      id
      price
      stock
      isAvailable
    }
  }
`;

const DELETE_EVENT_PRODUCT = /* GraphQL */ `
  mutation DeleteEventProduct($input: DeleteEventProductInput!) {
    deleteEventProduct(input: $input) {
      id
    }
  }
`;

const EVENT_PRODUCT_RESERVATIONS = /* GraphQL */ `
  query GetEventProductReservations($id: ID!) {
    getEventProduct(id: $id) {
      id
      stockReservations(limit: 1000) {
        items {
          id
          qty
          status
          boothNumber
          orderId
          eventId
          reservationDate
          reservationEndDate
          createdAt
          company {
            id
            name
          }
        }
      }
    }
  }
`;

// the lock row of a booth (boothlock-<eventId>#<NUMBER>): hold, admin option or order claim
const GET_STOCK_RESERVATION = /* GraphQL */ `
  query GetBoothLockReservation($id: ID!) {
    getStockReservation(id: $id) {
      id
      status
      boothNumber
      orderId
      reservationEndDate
      companyStockReservationsId
      eventProductStockReservationsId
    }
  }
`;

const DELETE_STOCK_RESERVATION = /* GraphQL */ `
  mutation DeleteStockReservation($input: DeleteStockReservationInput!) {
    deleteStockReservation(input: $input) {
      id
    }
  }
`;

const PRODUCTS_BY_TYPE = /* GraphQL */ `
  query ProductsByType($type: String!, $limit: Int, $nextToken: String) {
    productsByType(type: $type, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name {
          language
          text
        }
        sku
        type
        price
        stock
        boothNumber
        boothVariant
        boothVariantAliases
        boothMatchRule
        boothPriceMode
        boothArea
        boothWidth
        boothDepth
        image {
          main {
            fileName
          }
        }
        eventProducts(limit: 100) {
          items {
            id
            event {
              id
              name {
                language
                text
              }
            }
          }
        }
      }
      nextToken
    }
  }
`;

const PRODUCTS_BY_SKU = /* GraphQL */ `
  query ProductsBySkuForBooth($sku: String!, $limit: Int) {
    productsBySku(sku: $sku, limit: $limit) {
      items {
        id
      }
    }
  }
`;

const GET_TEMPLATE_PRODUCT = /* GraphQL */ `
  query GetBoothTemplateProduct($id: ID!) {
    getProduct(id: $id) {
      id
      name {
        language
        text
      }
      productCategoryProductsId
      allowedBuyerGroups
      price
      invoiceName {
        language
        text
      }
      boothHeight
      boothArea
      boothNumber
      boothVariant
      boothVariantAliases
      boothMatchRule
      boothPriceMode
      boothPriceRule
      boothDimensionFile {
        fileName
        s3Path
        type
      }
      booth3DModel {
        fileName
        s3Path
        type
      }
      image {
        main {
          fileName
          alt
        }
        additional {
          fileName
          alt
        }
      }
      includes {
        language
        text
      }
      description {
        language
        text
      }
      components(limit: 1000) {
        items {
          componentProductID
          qty
          order
        }
      }
    }
  }
`;

const LIST_PRODUCT_INDUSTRIES = /* GraphQL */ `
  query ListAvailableProductsInIndustries($filter: ModelAvailableProductsInIndustriesFilterInput, $limit: Int, $nextToken: String) {
    listAvailableProductsInIndustries(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        industryId
      }
      nextToken
    }
  }
`;

const CREATE_PRODUCT = /* GraphQL */ `
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      price
    }
  }
`;

const CREATE_PRODUCT_COMPONENT = /* GraphQL */ `
  mutation CreateProductComponent($input: CreateProductComponentInput!) {
    createProductComponent(input: $input) {
      id
    }
  }
`;

const CREATE_PRODUCT_INDUSTRY = /* GraphQL */ `
  mutation CreateAvailableProductsInIndustries($input: CreateAvailableProductsInIndustriesInput!) {
    createAvailableProductsInIndustries(input: $input) {
      id
    }
  }
`;

const DELETE_PRODUCT = /* GraphQL */ `
  mutation DeleteProduct($input: DeleteProductInput!) {
    deleteProduct(input: $input) {
      id
    }
  }
`;

const DELETE_PRODUCT_COMPONENT = /* GraphQL */ `
  mutation DeleteProductComponent($input: DeleteProductComponentInput!) {
    deleteProductComponent(input: $input) {
      id
    }
  }
`;

const DELETE_PRODUCT_INDUSTRY = /* GraphQL */ `
  mutation DeleteAvailableProductsInIndustries($input: DeleteAvailableProductsInIndustriesInput!) {
    deleteAvailableProductsInIndustries(input: $input) {
      id
    }
  }
`;

const SEARCH_COMPANIES = /* GraphQL */ `
  query SearchCompanies($filter: SearchableCompanyFilterInput, $limit: Int) {
    searchCompanies(filter: $filter, limit: $limit) {
      items {
        id
        name
      }
    }
  }
`;

const LIST_EVENT_BOOTH_PRODUCTS = /* GraphQL */ `
  query ListEventBoothProducts($filter: ModelEventProductFilterInput, $limit: Int, $nextToken: String) {
    listEventProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        price
        stock
        isAvailable
        sortKey
        createdAt
        boothPriceRule
        timeRangePrices {
          timeRange {
            start
            end
          }
          price
        }
        product {
          id
          type
          sku
          price
          boothNumber
          boothVariant
          boothVariantAliases
          boothMatchRule
          boothPriceMode
          boothPriceRule
          boothArea
          boothWidth
          boothDepth
          name {
            language
            text
          }
        }
      }
      nextToken
    }
  }
`;

const UPDATE_PRODUCT_BOOTH_NUMBER = /* GraphQL */ `
  mutation UpdateProductBoothNumber($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
      boothNumber
      boothVariant
    }
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Readable message of an Error or an Amplify GraphQL error response ({ errors: [...] }). */
export function errorMessage(error) {
  if (!error) return '';
  if (Array.isArray(error.errors) && error.errors.length > 0) {
    return error.errors.map((e) => e && e.message).filter(Boolean).join('; ');
  }
  if (typeof error.message === 'string') return error.message;
  return String(error);
}

const numOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Price of a booth created from a hall plan area. Single definition for the create modal's
 * preview and the creation itself. Per-m² pricing applies only with an area > 0 AND a price
 * per m² > 0; otherwise the fixed price (> 0) is used. Returns null when neither yields a
 * positive price, so no booth is ever created at 0 EUR by accident.
 *
 * @param {{ area: number|string|null, priceMode: 'perSqm'|'fixed', pricePerSqm: number|string|null, fixedPrice: number|string|null }} options
 * @returns {number|null}
 */
export function computeBoothPrice({ area, priceMode, pricePerSqm, fixedPrice }) {
  const sqm = numOrNull(area);
  const perSqm = numOrNull(pricePerSqm);
  if (priceMode === 'perSqm' && sqm !== null && sqm > 0 && perSqm !== null && perSqm > 0) {
    return Math.round(sqm * perSqm * 100) / 100;
  }
  const fixed = numOrNull(fixedPrice);
  return fixed !== null && fixed > 0 ? fixed : null;
}

/** true when the (template) product is priced per m² (Product.boothPriceMode 'perSqm'): its price is a rate. */
export const isPerSqmProduct = (product) => String(product?.boothPriceMode || '').trim() === 'perSqm';

/**
 * EventProduct price of a row of "create offers" (single definition for the form preview and the creation):
 * - the row price (> 0): with priceMode 'perSqm' it is a rate per m² and the stored price is rate × m² of
 *   the row (null without m²) - unless the row's template is priced per m² itself (Product.boothPriceMode
 *   'perSqm', copied to the new offer): then the rate is stored as it is, the booking multiplies by the size;
 * - else the price of the row's template product (copied like the other template fields);
 * - null when neither yields a positive price (no offer is ever created at 0 EUR by accident).
 *
 * @param {{ price: number|string|null, area: number|string|null, priceMode: 'fixed'|'perSqm', template?: object|null }} options
 * @returns {number|null}
 */
export function boothRowPrice({
  price, area, priceMode, template = null,
}) {
  const value = numOrNull(price);
  if (value !== null && value > 0) {
    if (priceMode === 'perSqm' && !isPerSqmProduct(template)) {
      return computeBoothPrice({
        area, priceMode: 'perSqm', pricePerSqm: value, fixedPrice: null,
      });
    }
    return value;
  }
  const templatePrice = numOrNull(template?.price);
  return templatePrice !== null && templatePrice > 0 ? templatePrice : null;
}

/** Booth products whose normalized boothNumber equals the given number. */
export function findBoothProductsByNumber(products, boothNumber) {
  const key = normalizeBoothNumber(boothNumber);
  if (!key) return [];
  return (products || []).filter((p) => p && normalizeBoothNumber(p.boothNumber) === key);
}

/** Booth number as stored on the product: trimmed, '' -> null. */
export function cleanBoothNumber(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
}

/** Variant code / alias as stored: trimmed, inner whitespace collapsed, '' -> null. */
export function cleanVariantCode(value) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  return text || null;
}

/**
 * Aliases of a variant product as stored: trimmed codes without duplicates and without the variant
 * code itself; null when none (never []).
 */
export function cleanVariantAliases(aliases, variant) {
  const seen = new Set([normalizeVariant(variant)].filter(Boolean));
  const list = [];
  (Array.isArray(aliases) ? aliases : []).forEach((alias) => {
    const code = cleanVariantCode(alias);
    const key = normalizeVariant(code);
    if (!code || seen.has(key)) return;
    seen.add(key);
    list.push(code);
  });
  return list.length > 0 ? list : null;
}

/** Normalized variant codes of a product: boothVariant + boothVariantAliases ([] for other products). */
export function variantCodesOf(product) {
  if (!product || !normalizeVariant(product.boothVariant)) return [];
  const codes = [product.boothVariant, ...(Array.isArray(product.boothVariantAliases) ? product.boothVariantAliases : [])]
    .map(normalizeVariant)
    .filter(Boolean);
  return [...new Set(codes)];
}

/** Variant products (boothVariant, no boothNumber) carrying one of the codes as boothVariant or alias. */
export function findBoothProductsByVariant(products, codes) {
  const keys = new Set((Array.isArray(codes) ? codes : [codes]).map(normalizeVariant).filter(Boolean));
  if (keys.size === 0) return [];
  return (products || []).filter((p) => isVariantProduct(p) && variantCodesOf(p).some((code) => keys.has(code)));
}

/**
 * Booth EventProducts of the event (loadEventBoothProducts rows) whose product is another variant product
 * sharing one of the codes (boothVariant or alias): a second product with the same code in one event is refused.
 */
export function findVariantConflicts(eventBoothProducts, productId, codes) {
  const seen = new Set();
  return findBoothProductsByVariant(
    (eventBoothProducts || []).map((ep) => ep && ep.product).filter(Boolean),
    codes,
  ).filter((product) => {
    if (product.id === productId || seen.has(product.id)) return false;
    seen.add(product.id);
    return true;
  });
}

/**
 * The event's booth products in the input shape of matchAreasToProducts / variantsOf (utils/boothMap):
 * [{ eventProductId, productId, boothNumber, boothVariant, boothVariantAliases, boothMatchRule, sortKey }].
 * sortKey = EventProduct.createdAt, exactly like boothLogic/placeOrder.boothProductDescriptor (the earlier
 * linked product wins a duplicate offer code) - NOT EventProduct.sortKey (display order only).
 * boothMatchRule is passed through raw (AWSJSON string or object; the matching validates it).
 */
export function toMatchProducts(eventBoothProducts) {
  return (eventBoothProducts || [])
    .filter((ep) => ep && ep.product)
    .map((ep) => ({
      eventProductId: ep.id,
      productId: ep.product.id,
      boothNumber: ep.product.boothNumber || null,
      boothVariant: ep.product.boothVariant || null,
      boothVariantAliases: ep.product.boothVariantAliases || null,
      boothMatchRule: ep.product.boothMatchRule ?? null,
      sortKey: ep.createdAt || null,
    }));
}

/** Standard position types of the plan vocabulary (decision 14.10.2026); free keys are valid too. */
export const STANDARD_POSITION_TYPES = ['row', 'corner', 'head', 'island'];

/**
 * Position type / zone as stored on an area: trimmed, inner whitespace collapsed, '' -> null. A standard
 * position key in another case ('Corner') is stored as the standard key; free keys keep the plan's spelling.
 */
export function cleanPositionType(value) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return null;
  const standard = STANDARD_POSITION_TYPES.find((key) => key === text.toLowerCase());
  return standard || text;
}

export function cleanZone(value) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  return text || null;
}

/** Comparison key of a feature (position type / zone): trimmed, lower case ('' for none). */
export const featureKey = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Booth number check of the editors (decision 14.10.2026: no fixed pattern, the numbers come from the SVG):
 * { ok, error, warnings } - error for an empty number or a leading '#' (reserved for unnamed shapes),
 * warnings 'id-whitespace' (inner whitespace, collapsed when matched) and 'id-too-long' (more than 32 characters).
 */
export function checkBoothNumber(value) {
  const text = String(value ?? '').trim();
  if (!text) return { ok: false, error: 'empty', warnings: [] };
  if (text.startsWith('#')) return { ok: false, error: 'reserved-id', warnings: [] };
  const warnings = [];
  if (/\s/.test(text)) warnings.push('id-whitespace');
  if (text.length > 32) warnings.push('id-too-long');
  return { ok: true, error: null, warnings };
}

/** Parsed AWSJSON rule object (match / price rule) or null; never throws. */
export function ruleObjectOrNull(value) {
  let parsed = value;
  for (let i = 0; i < 2 && typeof parsed === 'string'; i += 1) {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'parse-booth-rule-json',
        consoleMessage: 'Error reading a booth rule (AWSJSON)',
        context: { length: String(parsed).length },
      });
      return null;
    }
  }
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
}

/**
 * Rule offer: a booth product without booth number and without offer code that carries a match rule
 * (Product.boothMatchRule): it covers the plan booths whose features match the rule.
 */
export function isRuleOfferProduct(product) {
  return Boolean(product)
    && !cleanBoothNumber(product.boothNumber)
    && !cleanVariantCode(product.boothVariant)
    && ruleObjectOrNull(product.boothMatchRule) !== null;
}

/** Number of (named) areas carrying one of the variant codes of the product (0 for other products). */
export function countVariantAreas(areas, product) {
  const codes = new Set(variantCodesOf(product));
  if (codes.size === 0) return 0;
  return (areas || []).filter((area) => area
    && !isUnnamedBoothNumber(area.boothNumber)
    && codes.has(normalizeVariant(area.variant))).length;
}

/** Label of a booth product in messages: booth number, else variant code, else SKU / id. */
export function boothProductLabel(product) {
  if (!product) return '';
  return cleanBoothNumber(product.boothNumber) || cleanVariantCode(product.boothVariant) || product.sku || product.id || '';
}

/**
 * Booth map with the product-level fields copied onto every booth (the Lambda sends name, type, image,
 * files and sizes once per product in map.products): booth.name, type, area (product m², else the
 * variant size of the area), width, depth, height, image, dimensionFile, model3d. Fields a booth already
 * carries are kept.
 */
export function hydrateBoothMap(map) {
  if (!map || !Array.isArray(map.booths)) return map || null;
  const products = map.products && typeof map.products === 'object' ? map.products : {};
  const pick = (value, fallback) => (value === undefined || value === null ? fallback ?? null : value);
  return {
    ...map,
    booths: map.booths.map((booth) => {
      const product = (booth && products[booth.productId]) || null;
      if (!booth || !product) return booth;
      return {
        ...booth,
        name: pick(booth.name, product.name),
        type: pick(booth.type, product.type),
        area: pick(booth.area, pick(product.area, booth.variantSize)),
        width: pick(booth.width, product.width),
        depth: pick(booth.depth, product.depth),
        height: pick(booth.height, product.height),
        image: pick(booth.image, product.image),
        dimensionFile: pick(booth.dimensionFile, product.dimensionFile),
        model3d: pick(booth.model3d, product.model3d),
      };
    }),
  };
}

/**
 * Booth EventProducts of the event (loadEventBoothProducts rows: { id, product: { id, boothNumber, sku } })
 * whose product is another product with the same normalized booth number. Empty for an empty number.
 */
export function findBoothNumberConflicts(eventBoothProducts, productId, boothNumber) {
  const key = normalizeBoothNumber(boothNumber);
  if (!key) return [];
  const seen = new Set();
  return (eventBoothProducts || []).filter((ep) => {
    const product = ep && ep.product;
    if (!product || product.id === productId || seen.has(product.id)) return false;
    if (normalizeBoothNumber(product.boothNumber) !== key) return false;
    seen.add(product.id);
    return true;
  });
}

const isBoothType = (type) => BOOTH_PRODUCT_TYPES.includes(type);

// single booths (booth number) first, then variant products (code), then the rest (SKU); each in natural order
const compareBoothProductsForList = (a, b) => {
  const rank = (p) => {
    if (cleanBoothNumber(p?.boothNumber)) return 0;
    if (cleanVariantCode(p?.boothVariant)) return 1;
    return 2;
  };
  const aRank = rank(a);
  const bRank = rank(b);
  if (aRank !== bRank) return aRank - bRank;
  if (aRank === 0) return compareBoothNumbers(a.boothNumber, b.boothNumber);
  if (aRank === 1) return compareBoothNumbers(a.boothVariant, b.boothVariant);
  return String(a?.sku || '').localeCompare(String(b?.sku || ''), 'de', { numeric: true });
};

const sortEventBoothProducts = (list) => [...list].sort((a, b) => compareBoothProductsForList(a.product, b.product));

// local booth number edits win over list reads for this long (the reads are eventually consistent)
const BOOTH_NUMBER_EDIT_TTL_MS = 15000;
const DELAYED_RELOAD_MS = 2000;

/**
 * SKU scheme of booth products: 'CP-ST-' + the booth number (or the variant code of a variant product)
 * with every character outside [A-Za-z0-9.] removed (case kept), e.g. 'D3.5' -> 'CP-ST-D3.5',
 * 'A-12' -> 'CP-ST-A12', variant '18B' -> 'CP-ST-18B'.
 * When that SKU is already taken (productsBySku), '-2', '-3', ... is appended
 * (see createBoothProducts). Booth products created elsewhere must use the same scheme.
 */
export const BOOTH_SKU_PREFIX = 'CP-ST-';
export function boothSkuBase(boothNumber) {
  return `${BOOTH_SKU_PREFIX}${String(boothNumber || '').replace(/[^A-Za-z0-9.]/g, '')}`;
}

/**
 * Commercial product names of booths created from hall plan areas / stands. The booth number / variant
 * code stays separate (Product.boothNumber / boothVariant); the name is what appears on order and invoice,
 * e.g. "Ausstellungsstand (12)" or "Ausstellungsstand 18 m² (18B)". Placeholders: {{number}} (booth number,
 * '' for a variant), {{variant}} (variant code, '' for a single booth), {{area}} and {{size}} (m² without
 * unit, '' when unknown), {{count}} (booths of the variant), {{template}} (German name of the template
 * product, '' without template).
 */
export const BOOTH_NAME_PATTERN_AREA = 'Ausstellungsstand ({{area}})';
export const BOOTH_NAME_PATTERN_NUMBER = 'Stand {{number}}';
export const BOOTH_NAME_PATTERN_VARIANT = 'Ausstellungsstand {{size}} m² ({{variant}})';

/**
 * Default name pattern of a row: a variant row 'Ausstellungsstand {{size}} m² ({{variant}})'; a single
 * booth with m² 'Ausstellungsstand ({{area}})', otherwise 'Stand {{number}}'. Applied per row whenever the
 * name pattern is empty (the creation form starts empty), so entering the m² of a row switches that row
 * to the area pattern.
 */
export function defaultBoothNamePattern(area, variant = null) {
  if (cleanVariantCode(variant)) return BOOTH_NAME_PATTERN_VARIANT;
  const sqm = numOrNull(area);
  return sqm !== null && sqm > 0 ? BOOTH_NAME_PATTERN_AREA : BOOTH_NAME_PATTERN_NUMBER;
}

/** German text of a MultiLang list ([{ language, text }]); '' when there is none. */
export function germanText(list) {
  if (!Array.isArray(list)) return '';
  const entry = list.find((item) => item && item.language === 'de') || null;
  return String(entry?.text || '').trim();
}

const fillNamePattern = (pattern, values) => String(pattern)
  // an unknown size must not leave a lone unit behind ("Ausstellungsstand  m² (18B)")
  .replace(/\{\{\s*(size|area)\s*\}\}\s*m²/g, (match, key) => (values[key] ? `${values[key]} m²` : ''))
  .replace(/\{\{\s*(number|area|size|variant|count|template)\s*\}\}/g, (match, key) => values[key] ?? '')
  // an empty placeholder must not leave "()" or double spaces behind
  .replace(/\(\s*\)/g, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

/**
 * German + English product name of a booth from a name pattern (see BOOTH_NAME_PATTERN_*).
 * An empty pattern uses the row's default (defaultBoothNamePattern). The English name uses the same
 * pattern; a leading "Ausstellungsstand" becomes "Exhibition stand". When the filled pattern is empty,
 * 'Stand <number|variant>' / 'Booth <number|variant>' is used.
 *
 * @param {string} pattern
 * @param {{ boothNumber?: string, variant?: string, area: number|string|null, count?: number, templateName?: string }} values
 * @returns {{ de: string, en: string }}
 */
export function buildBoothProductNames(pattern, {
  boothNumber = '', variant = '', area, count = null, templateName = '',
}) {
  const number = String(boothNumber || '').trim();
  const code = String(variant || '').trim();
  const sqm = numOrNull(area);
  const source = String(pattern || '').trim() || defaultBoothNamePattern(sqm, code);
  const values = {
    number,
    variant: code,
    count: numOrNull(count) !== null ? String(numOrNull(count)) : '',
    template: String(templateName || '').trim(),
  };
  const sqmText = (locale) => (sqm !== null && sqm > 0 ? sqm.toLocaleString(locale, { maximumFractionDigits: 2 }) : '');
  const de = fillNamePattern(source, { ...values, area: sqmText('de-DE'), size: sqmText('de-DE') });
  const enSource = /^Ausstellungsstand/.test(source) ? source.replace(/^Ausstellungsstand/, 'Exhibition stand') : source;
  const en = fillNamePattern(enSource, { ...values, area: sqmText('en-GB'), size: sqmText('en-GB') });
  const label = number || code;
  return {
    de: de || `Stand ${label}`,
    en: en || `Booth ${label}`,
  };
}

/** m² of a version area (BoothPlanArea.size), from the SVG data-size; null when unknown. */
const sizeOrNull = (value) => {
  const n = numOrNull(value);
  return n !== null && n > 0 ? n : null;
};

/**
 * Preview of "Assign variants from list" (parseVariantAssignmentList result) against areas:
 * changes (area, current, next), unknown numbers (listed, no area), areas not in the list (named areas
 * the list does not mention; they keep their variant) and the codes with listed / found counts.
 *
 * @param {{assignments: Map<string, string>, codes: object[], errors: string[]}} parsed
 * @param {object[]} areas BoothPlanArea rows
 */
export function variantAssignmentPreview(parsed, areas) {
  const named = (areas || []).filter((area) => area && !isUnnamedBoothNumber(area.boothNumber));
  const byKey = new Map();
  named.forEach((area) => {
    const key = normalizeBoothNumber(area.boothNumber);
    if (key && !byKey.has(key)) byKey.set(key, area);
  });
  const assignments = parsed?.assignments instanceof Map ? parsed.assignments : new Map();
  const unknownNumbers = [];
  const changes = [];
  const found = new Map();
  assignments.forEach((code, key) => {
    const area = byKey.get(key);
    if (!area) {
      unknownNumbers.push(key);
      return;
    }
    found.set(code, (found.get(code) || 0) + 1);
    if (normalizeVariant(area.variant) !== code) {
      changes.push({ area, current: cleanVariantCode(area.variant), next: code });
    }
  });
  const notListed = named.filter((area) => !assignments.has(normalizeBoothNumber(area.boothNumber)));
  const codes = (parsed?.codes || []).map((entry) => ({
    ...entry,
    listed: entry.numbers.length,
    found: found.get(entry.code) || 0,
  }));
  changes.sort((a, b) => compareBoothNumbers(a.area.boothNumber, b.area.boothNumber));
  return {
    changes,
    unknownNumbers: unknownNumbers.sort(compareBoothNumbers),
    notListed: notListed.map((area) => area.boothNumber).sort(compareBoothNumbers),
    notListedWithoutVariant: notListed.filter((area) => !normalizeVariant(area.variant)).map((area) => area.boothNumber),
    codes,
    errors: parsed?.errors || [],
  };
}

/** openSides as stored: integer 1-4, else null. */
const openSidesOrNull = (value) => {
  const n = numOrNull(value);
  return n !== null && Number.isInteger(n) && n >= 1 && n <= 4 ? n : null;
};

/** attributes (AWSJSON or object) -> plain object with at least one key, else null. */
export function attributesOrNull(value) {
  const parsed = typeof value === 'string' ? ruleObjectOrNull(value) : value;
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0 ? parsed : null;
}

/** Stable JSON of an attributes object (sorted keys) for comparisons; '' for none. */
const attributesKey = (value) => {
  const obj = attributesOrNull(value);
  if (!obj) return '';
  return JSON.stringify(Object.keys(obj).sort().map((key) => [key, obj[key]]));
};

// features of an area besides the plan code and the size (Phase 1 of the booth offering model)
const AREA_FEATURE_FIELDS = [
  { field: 'positionType', clean: cleanPositionType, key: featureKey },
  { field: 'zone', clean: cleanZone, key: featureKey },
  { field: 'openSides', clean: openSidesOrNull, key: (v) => (v === null || v === undefined ? '' : String(v)) },
  { field: 'attributes', clean: attributesOrNull, key: attributesKey },
];

/**
 * Changes of "Re-read from the SVG": per area the SVG attributes of the stand with the same number -
 * data-variant (plan code), data-size and, when the parser delivers them, positionType (data-position),
 * zone (data-zone, also of a parent group), openSides (data-open-sides) and attributes (other data-*).
 * mode 'fill' only fills empty values, 'overwrite' replaces differing ones; a stand without an attribute
 * never clears a stored value.
 *
 * @param {Array<{area: object, svg: {variant, size, positionType?, zone?, openSides?, attributes?}|null}>} rows
 * @param {'fill'|'overwrite'} mode
 * @returns {Array<{area, current, next, currentSize, nextSize, features: Array<{field, current, next}>,
 *   update: {id, variant?, size?, positionType?, zone?, openSides?, attributes?}}>}
 */
export function svgVariantChanges(rows, mode) {
  const out = [];
  (rows || []).forEach(({ area, svg }) => {
    if (!area || !svg) return;
    const update = { id: area.id };
    const current = cleanVariantCode(area.variant);
    const next = cleanVariantCode(svg.variant);
    if (next && (mode === 'overwrite' ? normalizeVariant(current) !== normalizeVariant(next) : !current)) {
      update.variant = next;
    }
    const currentSize = sizeOrNull(area.size);
    const nextSize = sizeOrNull(svg.size);
    if (nextSize !== null && (mode === 'overwrite' ? currentSize !== nextSize : currentSize === null)) {
      update.size = nextSize;
    }
    const features = [];
    AREA_FEATURE_FIELDS.forEach(({ field, clean, key }) => {
      if (!(field in svg)) return;
      const currentValue = clean(area[field]);
      const nextValue = clean(svg[field]);
      if (nextValue === null) return;
      const differs = mode === 'overwrite' ? key(currentValue) !== key(nextValue) : currentValue === null;
      if (!differs) return;
      update[field] = nextValue;
      features.push({ field, current: currentValue, next: nextValue });
    });
    if (Object.keys(update).length <= 1) return;
    out.push({
      area,
      current,
      next: 'variant' in update ? update.variant : current,
      currentSize,
      nextSize: 'size' in update ? update.size : currentSize,
      features,
      update,
    });
  });
  return out.sort((a, b) => compareBoothNumbers(a.area.boothNumber, b.area.boothNumber));
}

/** Same as svgVariantChanges (all SVG attributes, not only the plan code). */
export const svgAreaChanges = svgVariantChanges;

/** Display text of a feature value (attributes as "key=value, …"). */
export function featureValueText(field, value) {
  if (value === null || value === undefined || value === '') return '';
  if (field === 'attributes') {
    const obj = attributesOrNull(value);
    return obj ? Object.keys(obj).sort().map((key) => `${key}=${obj[key]}`).join(', ') : '';
  }
  return String(value);
}

// ----- "Assign from list" with optional Position / Zone columns ------------------------------------

const HEADER_ALIASES = {
  number: ['nummer', 'number', 'nr', 'nr.', 'stand', 'booth', 'standnummer', 'booth number', 'boothnumber', 'stand number'],
  numbers: ['stände', 'staende', 'booths', 'numbers', 'standnummern', 'booth numbers', 'nummern', 'stands'],
  code: ['code', 'variante', 'variant', 'angebot', 'offer', 'plan code', 'plan-code', 'plancode', 'angebotscode', 'offer code', 'data-variant'],
  count: ['anzahl', 'count', 'menge', 'qty'],
  position: ['position', 'lage', 'positionstyp', 'position type', 'standtyp', 'data-position', 'typ', 'type'],
  zone: ['zone', 'zonen', 'bereich', 'area zone', 'data-zone'],
};

const headerKind = (cell) => {
  const text = String(cell || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return null;
  return Object.keys(HEADER_ALIASES).find((kind) => HEADER_ALIASES[kind].includes(text)) || null;
};

/**
 * Parses the "Assign from list" text. Without a header naming Position / Zone (or a single booth number
 * column) it is exactly parseVariantAssignmentList (one code per line: code, optional count, booth numbers).
 * With such a header row (columns separated by TAB or ';', any order), every line assigns per booth number
 * (column "Nummer"/"Number" = one booth, or "Stände"/"Booths" = comma list) the optional plan code
 * ("Code"/"Variante"/"Offer"), position type ("Position") and zone ("Zone"); empty cells change nothing.
 *
 * @returns {{assignments: Map<string, string>, codes: object[], features: Map<string, {positionType?: string, zone?: string}>,
 *   errors: string[], columns: string[]|null}} columns = recognised header kinds (null without header)
 */
export function parseAreaAssignmentList(text) {
  const lines = typeof text === 'string' ? text.split(/\r\n|\r|\n/) : [];
  const firstIndex = lines.findIndex((line) => line.trim());
  const headerCells = firstIndex >= 0 ? lines[firstIndex].split(/[\t;]/).map((c) => c.trim()) : [];
  const kinds = headerCells.map(headerKind);
  const hasFeatureColumn = kinds.includes('position') || kinds.includes('zone');
  const hasSingleNumberColumn = kinds[0] === 'number';
  const isHeader = kinds.length > 0 && kinds.every((kind, i) => kind || !headerCells[i])
    && (kinds.includes('number') || kinds.includes('numbers'))
    && (hasFeatureColumn || hasSingleNumberColumn);
  if (!isHeader) {
    return { ...parseVariantAssignmentList(text), features: new Map(), columns: null };
  }
  const col = (kind) => kinds.indexOf(kind);
  const numberCol = col('number') >= 0 ? col('number') : col('numbers');
  const listColumn = col('number') < 0;
  const codeCol = col('code');
  const positionCol = col('position');
  const zoneCol = col('zone');
  const assignments = new Map();
  const features = new Map();
  const codes = [];
  const byCode = new Map();
  const errors = [];
  lines.forEach((rawLine, index) => {
    if (index <= firstIndex || !rawLine.trim()) return;
    const label = `Line ${index + 1}`;
    const cells = rawLine.split(/[\t;]/).map((c) => c.trim());
    const cell = (i) => (i >= 0 && i < cells.length ? cells[i] : '');
    const numberText = cell(numberCol);
    const numbers = (listColumn ? numberText.split(',') : [numberText])
      .map((n) => String(n || '').trim().replace(/\s+/g, ' '))
      .filter(Boolean);
    if (numbers.length === 0) {
      errors.push(`${label}: no booth number`);
      return;
    }
    const rawCode = cell(codeCol);
    if (rawCode && !isValidVariantCode(rawCode)) {
      errors.push(`${label}: "${rawCode}" is not a valid plan code`);
      return;
    }
    const code = rawCode ? normalizeVariant(rawCode) : '';
    const positionType = cleanPositionType(cell(positionCol));
    const zone = cleanZone(cell(zoneCol));
    let entry = null;
    if (code) {
      entry = byCode.get(code);
      if (!entry) {
        entry = { code, count: null, numbers: [] };
        byCode.set(code, entry);
        codes.push(entry);
      }
    }
    numbers.forEach((number) => {
      if (number.startsWith('#')) {
        errors.push(`${label}: booth "${number}" is a placeholder number (reserved for unnamed shapes)`);
        return;
      }
      const key = normalizeBoothNumber(number);
      if (code) {
        const assigned = assignments.get(key);
        if (assigned === undefined) {
          assignments.set(key, code);
          entry.numbers.push(number);
        } else if (assigned !== code) {
          errors.push(`${label}: booth "${number}" is already listed with plan code "${assigned}"`);
        }
      }
      if (positionType || zone) {
        const previous = features.get(key) || {};
        features.set(key, {
          ...previous,
          ...(positionType ? { positionType } : {}),
          ...(zone ? { zone } : {}),
        });
      }
    });
  });
  return {
    assignments, codes, features, errors, columns: kinds.filter(Boolean),
  };
}

/**
 * Preview of "Assign from list" (parseAreaAssignmentList result) against areas: the plan-code part of
 * variantAssignmentPreview plus featureChanges [{ area, field, current, next }] (position type / zone that differ)
 * and `updates` [{ id, boothNumber, variant?, positionType?, zone? }] = one write per changed area.
 * With a header row (`headerMode`) the areas not in the list are not reported (notListed = []).
 */
export function areaAssignmentPreview(parsed, areas) {
  const base = variantAssignmentPreview(parsed, areas);
  const features = parsed?.features instanceof Map ? parsed.features : new Map();
  const byKey = new Map();
  (areas || []).forEach((area) => {
    if (!area || isUnnamedBoothNumber(area.boothNumber)) return;
    const key = normalizeBoothNumber(area.boothNumber);
    if (key && !byKey.has(key)) byKey.set(key, area);
  });
  const featureChanges = [];
  const unknownFeatureNumbers = [];
  features.forEach((values, key) => {
    const area = byKey.get(key);
    if (!area) {
      if (!parsed.assignments?.has(key)) unknownFeatureNumbers.push(key);
      return;
    }
    if (values.positionType && featureKey(values.positionType) !== featureKey(area.positionType)) {
      featureChanges.push({
        area, field: 'positionType', current: cleanPositionType(area.positionType), next: values.positionType,
      });
    }
    if (values.zone && featureKey(values.zone) !== featureKey(area.zone)) {
      featureChanges.push({
        area, field: 'zone', current: cleanZone(area.zone), next: values.zone,
      });
    }
  });
  const updatesById = new Map();
  const updateFor = (area) => {
    if (!updatesById.has(area.id)) updatesById.set(area.id, { id: area.id, boothNumber: area.boothNumber });
    return updatesById.get(area.id);
  };
  base.changes.forEach((change) => {
    updateFor(change.area).variant = change.next;
  });
  featureChanges.forEach((change) => {
    updateFor(change.area)[change.field] = change.next;
  });
  featureChanges.sort((a, b) => compareBoothNumbers(a.area.boothNumber, b.area.boothNumber));
  // header mode lists single booths (often a few corrections): the untouched rest of the plan is no information
  // (review 2, C-9) - only the listed booths and their changes are shown
  const headerMode = Array.isArray(parsed?.columns);
  return {
    ...base,
    unknownNumbers: [...new Set([...base.unknownNumbers, ...unknownFeatureNumbers])].sort(compareBoothNumbers),
    notListed: headerMode ? [] : base.notListed,
    notListedWithoutVariant: headerMode ? [] : base.notListedWithoutVariant,
    featureChanges,
    updates: [...updatesById.values()],
    hasFeatures: features.size > 0,
    headerMode,
  };
}

// ----- Carry-over of area corrections when another version becomes live --------------------------

/**
 * Manual corrections of the live version that the new version would lose, per booth number (only booth
 * numbers present in both versions): bookable = false (taken off sale), a plan code that differs from the
 * new version's code when the new area has none or the live code was set by hand (differs from the live
 * version's SVG, `svgVariantByKey` = normalized number -> SVG code of the live version; unknown = not manual),
 * and position type / zone when the new area lacks them.
 *
 * @returns {Array<{key, boothNumber, areaId, field: 'bookable'|'variant'|'positionType'|'zone', current, next}>}
 *   areaId = the area of the NEW version; current = its value, next = the live version's value
 */
export function areaCarryOverCorrections(liveAreas, newAreas, { svgVariantByKey = null } = {}) {
  const named = (list) => {
    const out = new Map();
    (list || []).forEach((area) => {
      if (!area || isUnnamedBoothNumber(area.boothNumber)) return;
      const key = normalizeBoothNumber(area.boothNumber);
      if (key && !out.has(key)) out.set(key, area);
    });
    return out;
  };
  const nextByKey = named(newAreas);
  const corrections = [];
  named(liveAreas).forEach((live, key) => {
    const target = nextByKey.get(key);
    if (!target) return;
    const base = { key, boothNumber: target.boothNumber, areaId: target.id };
    if (live.bookable === false && target.bookable !== false) {
      corrections.push({
        ...base, field: 'bookable', current: true, next: false,
      });
    }
    const liveCode = cleanVariantCode(live.variant);
    const targetCode = cleanVariantCode(target.variant);
    if (liveCode && normalizeVariant(liveCode) !== normalizeVariant(targetCode)) {
      const svgCode = svgVariantByKey instanceof Map ? svgVariantByKey.get(key) : undefined;
      const manual = svgVariantByKey instanceof Map && normalizeVariant(svgCode) !== normalizeVariant(liveCode);
      if (!targetCode || manual) {
        corrections.push({
          ...base, field: 'variant', current: targetCode, next: liveCode,
        });
      }
    }
    const livePosition = cleanPositionType(live.positionType);
    if (livePosition && !cleanPositionType(target.positionType)) {
      corrections.push({
        ...base, field: 'positionType', current: null, next: livePosition,
      });
    }
    const liveZone = cleanZone(live.zone);
    if (liveZone && !cleanZone(target.zone)) {
      corrections.push({
        ...base, field: 'zone', current: null, next: liveZone,
      });
    }
  });
  return corrections.sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber) || a.field.localeCompare(b.field));
}

/** Area writes ([{ id, boothNumber, field: value }]) of the selected carry-over corrections, one per area. */
export function carryOverUpdates(corrections) {
  const byId = new Map();
  (corrections || []).forEach((c) => {
    if (!c || !c.areaId) return;
    if (!byId.has(c.areaId)) byId.set(c.areaId, { id: c.areaId, boothNumber: c.boothNumber });
    byId.get(c.areaId)[c.field] = c.next;
  });
  return [...byId.values()];
}

// ----- Features of the plan (report section "Features in the plan") -------------------------------

/**
 * Stands / areas grouped by size × position type × zone: [{ key, size, positionType, zone, count, boothNumbers }]
 * (named stands only; sizes ascending, then position type and zone; `null` = feature not set).
 */
export function featureCombinations(stands) {
  const groups = new Map();
  (stands || []).forEach((stand) => {
    const number = stand && (stand.id ?? stand.boothNumber);
    if (!number || isUnnamedBoothNumber(number)) return;
    const size = sizeOrNull(stand.size);
    const positionType = cleanPositionType(stand.positionType);
    const zone = cleanZone(stand.zone);
    const key = `${size ?? ''}|${featureKey(positionType)}|${featureKey(zone)}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key, size, positionType, zone, count: 0, boothNumbers: [],
      });
    }
    const group = groups.get(key);
    group.count += 1;
    group.boothNumbers.push(String(number));
  });
  const text = (v) => String(v ?? '￿');
  return [...groups.values()]
    .map((group) => ({ ...group, boothNumbers: group.boothNumbers.sort(compareBoothNumbers) }))
    .sort((a, b) => (a.size ?? Infinity) - (b.size ?? Infinity)
      || text(a.positionType).localeCompare(text(b.positionType))
      || text(a.zone).localeCompare(text(b.zone)));
}

/** Throws the Lambda's message when a BoothActionResult reports a failure (business outcomes do not throw). */
/** true for the single lock row of a booth (id boothlock-<eventId>#<NUMBER>), released through the Lambda. */
function isLockRowId(row) {
  return String(row?.id || '').startsWith('boothlock-');
}

/** Admin rows a release may delete directly: holds, options and rows without status (never order claims). */
function isReleasableAdminRow(row) {
  return !!row && (row.status == null || row.status === 'hold' || row.status === 'active');
}

function assertActionResult(result, fallback) {
  if (result && result.success) return result;
  throw new Error((result && (result.message || result.code)) || fallback);
}

const fileInputOrNull = (file) => (file && file.s3Path
  ? { fileName: file.fileName || null, s3Path: file.s3Path, type: file.type || null }
  : null);

/** BoothPlan -> BoothPlanInput (drops __typename and unknown keys). */
function toBoothPlanInput(plan) {
  if (!plan) return null;
  return {
    image: fileInputOrNull(plan.image),
    width: numOrNull(plan.width),
    height: numOrNull(plan.height),
    areasUpdatedAt: plan.areasUpdatedAt || null,
    viewBox: plan.viewBox || null,
    activeVersionId: plan.activeVersionId || null,
  };
}

// ----- Hall plan versions (SVG import) ---------------------------------------

export const HALL_PLAN_SVG_MAX_BYTES = 10 * 1024 * 1024;
const SVG_CONTENT_TYPE = 'image/svg+xml';
// DynamoDB items are limited to 400 KB: the stored issue list is capped (errors come first)
const MAX_ISSUES_JSON_LENGTH = 300000;

/** AWSJSON value (JSON string, possibly double encoded, or already an object) -> value; fallback when unreadable. */
export function parseAwsJson(value, fallback) {
  let parsed = value;
  for (let i = 0; i < 2 && typeof parsed === 'string'; i += 1) {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'parse-hall-plan-version-json',
        consoleMessage: 'Error reading a JSON field of a hall plan version',
        context: { length: String(parsed).length },
      });
      return fallback;
    }
  }
  return parsed === null || parsed === undefined || typeof parsed === 'string' ? fallback : parsed;
}

/** HallPlanVersion as returned by the API -> AWSJSON fields parsed (summary {}, issues [], matchSummary object|null). */
function normalizeVersion(version) {
  if (!version) return null;
  const issues = parseAwsJson(version.issues, []);
  return {
    ...version,
    summary: parseAwsJson(version.summary, {}),
    issues: Array.isArray(issues) ? issues : [],
    matchSummary: parseAwsJson(version.matchSummary, null),
  };
}

/**
 * Whether a hall plan version may be activated, from its stored issues: every error except 'missing-id'
 * blocks (shapes without a booth number become unbookable placeholder areas); warnings never block.
 * The stored issue list may be shortened (issuesForStorage): errors of errorCount that are neither a
 * stored blocker nor an unnamed shape are unknown and block too (pseudo blocker 'issues-truncated').
 *
 * @param {object} version HallPlanVersion (normalized or raw AWSJSON fields)
 * @returns {{ canActivate: boolean, blockers: object[], unnamed: number }}
 */
export function versionActivationState(version) {
  if (!version) return { canActivate: false, blockers: [], unnamed: 0 };
  const rawIssues = Array.isArray(version.issues) ? version.issues : parseAwsJson(version.issues, []);
  const issues = Array.isArray(rawIssues) ? rawIssues : [];
  const summary = version.summary && typeof version.summary === 'object' ? version.summary : parseAwsJson(version.summary, {});
  const blockers = activationBlockers(issues);
  const unnamed = numOrNull(summary?.unnamed) ?? unnamedCount(issues);
  const unknownErrors = Math.max(0, (numOrNull(version.errorCount) ?? 0) - blockers.length - unnamed);
  const allBlockers = unknownErrors > 0
    ? [...blockers, { level: 'error', code: 'issues-truncated', message: `${unknownErrors} errors were not stored with the version.` }]
    : blockers;
  return { canActivate: allBlockers.length === 0, blockers: allBlockers, unnamed };
}

/** Distinct codes of blocking issues, e.g. 'invalid-id, duplicate-id' (at most `max`, then '…'). */
export function blockerCodes(blockers, max = 5) {
  const codes = [...new Set((blockers || []).map((issue) => issue && issue.code).filter(Boolean))];
  return codes.length > max ? `${codes.slice(0, max).join(', ')}, …` : codes.join(', ');
}

const sortVersions = (list) => [...list].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

/** JSON of the issue list, shortened (with a closing hint) when it would not fit into the version row. */
function issuesForStorage(issues) {
  const list = Array.isArray(issues) ? issues : [];
  let json = JSON.stringify(list);
  let keep = list.length;
  while (json.length > MAX_ISSUES_JSON_LENGTH && keep > 0) {
    keep = Math.floor(keep * 0.8);
    json = JSON.stringify([
      ...list.slice(0, keep),
      { level: 'warning', code: 'issues-truncated', message: `${list.length - keep} further issues were not stored.` },
    ]);
  }
  return json;
}

/** File name without directory and extension, e.g. 'hallenplan_2028.svg' -> 'hallenplan_2028'. */
function baseFileName(fileName) {
  const name = String(fileName || 'hallplan').split(/[\\/]/).pop();
  return name.replace(/\.[^.]+$/, '') || 'hallplan';
}

/** File object for uploadBoothAsset from SVG text. */
function svgFile(text, name) {
  const blob = new Blob([text], { type: SVG_CONTENT_TYPE });
  return new File([blob], name, { type: SVG_CONTENT_TYPE });
}

const readFileText = (file) => (typeof file.text === 'function'
  ? file.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsText(file);
  }));

const multiLangInput = (list) => (Array.isArray(list)
  ? list.filter(Boolean).map(({ language, text }) => ({ language, text }))
  : null);

const imageInput = (image) => (image && image.fileName ? { fileName: image.fileName, alt: image.alt || null } : null);

/** AWSJSON input value: a JSON string as returned by the API is passed on, an object is serialized, empty -> null. */
const awsJsonInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return typeof value === 'string' ? value : JSON.stringify(value);
};

/** Client-side id of a new product (its booth files are copied into public/booth-assets/<id>/ before it is created). */
const newProductId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`);

function multiImageInput(image) {
  if (!image) return null;
  const main = imageInput(image.main);
  const additional = Array.isArray(image.additional) ? image.additional.map(imageInput).filter(Boolean) : [];
  if (!main && additional.length === 0) return null;
  return { main, additional };
}

/**
 * Runs `fn` for every item, CHUNK_SIZE at a time. Never rejects: returns the failures
 * ({ item, error }) so the caller can report them.
 */
async function runInChunks(items, fn) {
  const failures = [];
  for (let i = 0; i < items.length; i += CHUNK_SIZE) {
    const chunk = items.slice(i, i + CHUNK_SIZE);
    const results = await Promise.all(chunk.map((item) => fn(item).then(
      () => null,
      (error) => ({ item, error }),
    )));
    results.forEach((result) => {
      if (result) failures.push(result);
    });
  }
  return failures;
}

/** Re-reads of the byPlanVersion GSI before an activation is refused (GSI reads are eventually consistent). */
const AREA_CHECK_RETRIES = 3;
const AREA_CHECK_DELAY_MS = 1000;
const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * All pages of a list query. allowPartial: keep the data of a response with GraphQL errors (Amplify v6
 * rejects them), e.g. an EventProduct whose event was deleted (non-null field nulled by AppSync); the
 * broken items arrive as null and are dropped.
 */
async function queryAllPages(query, variables, fieldName, { allowPartial = false } = {}) {
  const items = [];
  let nextToken = null;
  do {
    let response;
    try {
      response = await client.graphql({ query, variables: { ...variables, limit: PAGE_LIMIT, nextToken } });
    } catch (error) {
      if (!allowPartial || !error?.data?.[fieldName]) throw error;
      captureError(error, {
        feature: FEATURE,
        operation: 'query-partial-data',
        consoleMessage: `Query ${fieldName} returned partial data`,
        context: { variables },
        tags: { partial: 'true' },
      });
      response = error;
    }
    const page = response.data[fieldName];
    items.push(...((page && page.items) || []).filter(Boolean));
    nextToken = page ? page.nextToken : null;
  } while (nextToken);
  return items;
}

// real booth numbers first, placeholder areas of unnamed shapes ('#<n>') last; each group in natural order
const sortAreas = (list) => [...list].sort((a, b) => {
  const aUnnamed = isUnnamedBoothNumber(a.boothNumber);
  const bUnnamed = isUnnamedBoothNumber(b.boothNumber);
  if (aUnnamed !== bUnnamed) return aUnnamed ? 1 : -1;
  return compareBoothNumbers(a.boothNumber, b.boothNumber);
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEventBoothPlan(eventId, isActive) {
  const [event, setEvent] = useState(null);
  const [planImageUrl, setPlanImageUrl] = useState('');
  // every BoothPlanArea row of the event (legacy rows + rows of all hall plan versions)
  const [allAreas, setAllAreas] = useState([]);
  const [versions, setVersions] = useState(null);
  const [boothMap, setBoothMap] = useState(null);
  const [boothProducts, setBoothProducts] = useState(null);
  // every booth-type EventProduct of the event, also those whose product has no booth number
  // (the booth map lists only products with a number)
  const [eventBoothProducts, setEventBoothProducts] = useState(null);
  // m² / width / depth per normalized booth number from the last area import of this session
  // (BoothPlanArea does not store them; used by "Create booths from unassigned areas")
  const [areaMeta, setAreaMeta] = useState({});
  const [loading, setLoading] = useState({
    event: false, areas: false, map: false, products: false, versions: false, eventProducts: false,
  });
  const [errors, setErrors] = useState({
    event: null, areas: null, map: null, products: null, versions: null, eventProducts: null,
  });
  const loadedFor = useRef(null);
  const eventBoothProductsRef = useRef(null);
  eventBoothProductsRef.current = eventBoothProducts;
  // productId -> { boothNumber, at } of booth number edits of this session (see BOOTH_NUMBER_EDIT_TTL_MS)
  const boothNumberEdits = useRef(new Map());
  const eventRef = useRef(null);
  eventRef.current = event;
  // pending delayed booth reloads (timer id -> resolve) and their generation, bumped when the event changes
  // or the hook unmounts (see reloadBoothsDelayed)
  const delayedReloads = useRef(new Map());
  const delayedReloadGeneration = useRef(0);

  // live areas: the rows of the active hall plan version, or the legacy rows (no planVersionId) without one
  const activeVersionId = event?.boothPlan?.activeVersionId || null;
  const areas = useMemo(
    () => allAreas.filter((area) => (activeVersionId ? area.planVersionId === activeVersionId : !area.planVersionId)),
    [allAreas, activeVersionId],
  );
  const areasRef = useRef(areas);
  areasRef.current = areas;
  const activeVersionIdRef = useRef(activeVersionId);
  activeVersionIdRef.current = activeVersionId;

  const boothProductsRef = useRef(null);

  const setLoadingKey = useCallback((key, value) => setLoading((prev) => ({ ...prev, [key]: value })), []);
  const setErrorKey = useCallback((key, value) => setErrors((prev) => ({ ...prev, [key]: value })), []);

  // ----- Loaders -----------------------------------------------------------

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    setLoadingKey('event', true);
    setErrorKey('event', null);
    try {
      const response = await client.graphql({ query: GET_EVENT_BOOTH_PLAN, variables: { id: eventId } });
      setEvent(response.data.getEvent || null);
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-event-booth-plan',
        consoleMessage: 'Error loading the event hall plan',
        context: { eventId },
      });
      setErrorKey('event', errorMessage(error));
    } finally {
      setLoadingKey('event', false);
    }
  }, [eventId, setErrorKey, setLoadingKey]);

  const loadAreas = useCallback(async () => {
    if (!eventId) return;
    setLoadingKey('areas', true);
    setErrorKey('areas', null);
    try {
      const items = await queryAllPages(BOOTH_PLAN_AREAS_BY_EVENT, { eventId }, 'boothPlanAreasByEvent');
      setAllAreas(sortAreas(items));
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-booth-plan-areas',
        consoleMessage: 'Error loading the hall plan areas',
        context: { eventId },
      });
      setErrorKey('areas', errorMessage(error));
    } finally {
      setLoadingKey('areas', false);
    }
  }, [eventId, setErrorKey, setLoadingKey]);

  /**
   * Reloads the booth map; resolves to the loaded map (null when it failed).
   * Optional `isCurrent()` is checked after the read: when it returns false the result is not applied (null).
   */
  const loadMap = useCallback(async ({ silent = false, isCurrent = null } = {}) => {
    if (!eventId) return null;
    if (!silent) setLoadingKey('map', true);
    setErrorKey('map', null);
    try {
      // product-level fields (name, type, m², files) are sent once per product: copied onto every booth
      const map = hydrateBoothMap(await fetchEventBoothMap({ eventId }));
      if (isCurrent && !isCurrent()) return null;
      setBoothMap(map || null);
      return map || null;
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-event-booth-map',
        consoleMessage: 'Error loading the booth map',
        context: { eventId },
      });
      if (!isCurrent || isCurrent()) setErrorKey('map', errorMessage(error));
      return null;
    } finally {
      if (!silent) setLoadingKey('map', false);
    }
  }, [eventId, setErrorKey, setLoadingKey]);

  /**
   * All EventProducts of this event whose product is a booth (event_booth | event_space), also those
   * without a booth number (legacy variant products). Booth numbers edited in this session in the
   * last BOOTH_NUMBER_EDIT_TTL_MS win over the (eventually consistent) read.
   * Resolves to the list (null when it failed). Optional `isCurrent()`: see loadMap.
   */
  const loadEventBoothProducts = useCallback(async ({ silent = false, isCurrent = null } = {}) => {
    if (!eventId) return null;
    if (!silent) setLoadingKey('eventProducts', true);
    setErrorKey('eventProducts', null);
    try {
      const items = await queryAllPages(
        LIST_EVENT_BOOTH_PRODUCTS,
        { filter: { eventEventProductsId: { eq: eventId } } },
        'listEventProducts',
      );
      if (isCurrent && !isCurrent()) return null;
      const now = Date.now();
      const list = sortEventBoothProducts(items
        .filter((ep) => ep.product && isBoothType(ep.product.type))
        .map((ep) => {
          const edit = boothNumberEdits.current.get(ep.product.id);
          if (!edit || now - edit.at > BOOTH_NUMBER_EDIT_TTL_MS) return ep;
          return { ...ep, product: { ...ep.product, boothNumber: edit.boothNumber } };
        }));
      setEventBoothProducts(list);
      return list;
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-event-booth-products',
        consoleMessage: 'Error loading the booth products of the event',
        context: { eventId },
      });
      if (!isCurrent || isCurrent()) setErrorKey('eventProducts', errorMessage(error));
      return null;
    } finally {
      if (!silent) setLoadingKey('eventProducts', false);
    }
  }, [eventId, setErrorKey, setLoadingKey]);

  /**
   * Booth map + booth EventProducts of the event (after changes of booths / booth numbers).
   * Optional `isCurrent()`: see loadMap.
   */
  const reloadBooths = useCallback(async ({ isCurrent = null } = {}) => {
    const [map] = await Promise.all([
      loadMap({ silent: true, isCurrent }),
      loadEventBoothProducts({ silent: true, isCurrent }),
    ]);
    return map;
  }, [loadEventBoothProducts, loadMap]);

  /**
   * reloadBooths once more after DELAYED_RELOAD_MS (the reads are eventually consistent). Resolves to the
   * reloaded map, or null when the reload failed or was dropped: pending reloads are cancelled when the
   * event changes or the hook unmounts, and the result is only applied while the event is still the loaded one.
   */
  const reloadBoothsDelayed = useCallback(() => new Promise((resolve) => {
    const forEventId = eventId;
    const generation = delayedReloadGeneration.current;
    const isCurrent = () => delayedReloadGeneration.current === generation && loadedFor.current === forEventId;
    const timer = setTimeout(() => {
      delayedReloads.current.delete(timer);
      if (!isCurrent()) {
        resolve(null);
        return;
      }
      reloadBooths({ isCurrent }).then(resolve, (error) => {
        captureError(error, {
          feature: FEATURE,
          operation: 'delayed-reload-booths',
          consoleMessage: 'Error in the delayed reload of the booths',
          context: { eventId: forEventId },
        });
        resolve(null);
      });
    }, DELAYED_RELOAD_MS);
    delayedReloads.current.set(timer, resolve);
  }), [eventId, reloadBooths]);

  // drop pending delayed reloads when the event changes and on unmount
  useEffect(() => () => {
    delayedReloadGeneration.current += 1;
    delayedReloads.current.forEach((resolve, timer) => {
      clearTimeout(timer);
      resolve(null);
    });
    delayedReloads.current = new Map();
  }, [eventId]);

  /** All HallPlanVersions of the event, newest first (AWSJSON fields parsed). Throws. */
  const listVersions = useCallback(async () => {
    const items = await queryAllPages(HALL_PLAN_VERSIONS_BY_EVENT, { eventId }, 'hallPlanVersionsByEvent');
    return sortVersions(items.map(normalizeVersion).filter(Boolean));
  }, [eventId]);

  const loadVersions = useCallback(async () => {
    if (!eventId) return;
    setLoadingKey('versions', true);
    setErrorKey('versions', null);
    try {
      setVersions(await listVersions());
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-hall-plan-versions',
        consoleMessage: 'Error loading the hall plan versions',
        context: { eventId },
      });
      setErrorKey('versions', errorMessage(error));
    } finally {
      setLoadingKey('versions', false);
    }
  }, [eventId, listVersions, setErrorKey, setLoadingKey]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadEvent(), loadAreas(), loadMap(), loadVersions(), loadEventBoothProducts()]);
  }, [loadEvent, loadAreas, loadMap, loadVersions, loadEventBoothProducts]);

  // first activation per event (MDB keeps the pane mounted)
  useEffect(() => {
    if (!isActive || !eventId || loadedFor.current === eventId) return;
    loadedFor.current = eventId;
    setEvent(null);
    setAllAreas([]);
    setVersions(null);
    setBoothMap(null);
    setBoothProducts(null);
    boothProductsRef.current = null;
    setEventBoothProducts(null);
    boothNumberEdits.current = new Map();
    setAreaMeta({});
    refreshAll();
  }, [isActive, eventId, refreshAll]);

  // signed URL of the plan image
  const planS3Path = event?.boothPlan?.image?.s3Path || '';
  useEffect(() => {
    let cancelled = false;
    if (!planS3Path) {
      setPlanImageUrl('');
      return undefined;
    }
    getBoothAssetUrl(planS3Path)
      .then((url) => {
        if (!cancelled) setPlanImageUrl(url);
      })
      .catch((error) => {
        captureError(error, {
          feature: FEATURE,
          operation: 'get-booth-plan-image-url',
          consoleMessage: 'Error loading the hall plan image URL',
          context: { eventId, s3Path: planS3Path },
        });
        if (!cancelled) setPlanImageUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [planS3Path, eventId]);

  // ----- Event.boothPlan -----------------------------------------------------

  /** Saves Event.boothPlan (null removes it) and returns the saved plan. */
  const saveBoothPlan = useCallback(async (plan) => {
    const response = await client.graphql({
      query: UPDATE_EVENT_BOOTH_PLAN,
      variables: { input: { id: eventId, boothPlan: plan ? toBoothPlanInput(plan) : null } },
    });
    const saved = response.data.updateEvent?.boothPlan || null;
    setEvent((prev) => ({ ...(prev || { id: eventId }), boothPlan: saved }));
    return saved;
  }, [eventId]);

  /**
   * Current Event.boothPlan read from the server right before a write ({} when the event has none).
   * Every write sends the whole BoothPlan object, so building it from a missing or stale local
   * copy would overwrite image/width/height with nulls. Throws when the event cannot be read.
   */
  const readCurrentPlan = useCallback(async () => {
    const response = await client.graphql({ query: GET_EVENT_BOOTH_PLAN, variables: { id: eventId } });
    const fresh = response.data.getEvent;
    if (!fresh) throw new Error(`Event ${eventId} not found`);
    return fresh.boothPlan || {};
  }, [eventId]);

  /**
   * True when the stored file belongs to a hall plan version (source or background SVG). Such files
   * are deleted with their version only, never when the plan image is replaced or removed.
   * Unknown (versions not readable) counts as true: keeping a file is harmless, deleting it is not.
   */
  const isVersionFile = useCallback(async (s3Path) => {
    if (!s3Path) return false;
    try {
      const list = await listVersions();
      return list.some((v) => v.sourceFile?.s3Path === s3Path || v.backgroundFile?.s3Path === s3Path);
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'check-hall-plan-version-file',
        consoleMessage: 'Error checking whether a file belongs to a hall plan version',
        context: { eventId, s3Path },
      });
      return true;
    }
  }, [eventId, listVersions]);

  /**
   * Sets every version with status 'active' (except `exceptId`) to 'archived'. The live version is
   * Event.boothPlan.activeVersionId; the status only mirrors it, so failures are reported, not thrown.
   */
  const archiveActiveVersions = useCallback(async (exceptId) => {
    let list = [];
    try {
      list = await listVersions();
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'archive-hall-plan-versions-list',
        consoleMessage: 'Error reading the hall plan versions before archiving',
        context: { eventId, exceptId },
      });
      return;
    }
    const targets = list.filter((v) => v.status === 'active' && v.id !== exceptId);
    const failures = await runInChunks(targets, (version) => client.graphql({
      query: UPDATE_HALL_PLAN_VERSION,
      variables: { input: { id: version.id, status: 'archived' } },
    }));
    if (failures.length) {
      captureError(failures[0].error, {
        feature: FEATURE,
        operation: 'archive-hall-plan-versions',
        consoleMessage: 'Error archiving the previous hall plan version',
        context: { eventId, exceptId, failed: failures.length },
      });
    }
  }, [eventId, listVersions]);

  /** Coordinate size of a plan file (SVG viewBox / width+height, or the image size); null when unknown. */
  const readPlanFileDimensions = useCallback(async (file) => {
    const isSvg = /\.svg$/i.test(file.name || '');
    return isSvg ? readSvgDimensions(file) : readImageDimensions(file);
  }, []);

  /**
   * Uploads a new plan image, saves it with its coordinate size and deletes the previous file.
   * keepDimensions=true keeps the stored width/height (existing areas stay aligned); otherwise the
   * size of the new file is used (falling back to the stored size when the file has none).
   */
  const uploadPlanImage = useCallback(async (file, onProgress, { dims: knownDims, keepDimensions = false } = {}) => {
    const dims = knownDims || await readPlanFileDimensions(file);
    const stored = await uploadBoothAsset(file, {
      keyPrefix: `booth-plans/${eventId}`,
      contentType: contentTypeForFileName(file.name),
      onProgress,
    });
    const current = await readCurrentPlan();
    const previousPath = current.image?.s3Path || null;
    const newSize = !keepDimensions && dims.width && dims.height
      && (Math.abs(dims.width - (Number(current.width) || 0)) > 0.5 || Math.abs(dims.height - (Number(current.height) || 0)) > 0.5);
    const saved = await saveBoothPlan({
      ...current,
      image: stored,
      width: (!keepDimensions && dims.width) || current.width || null,
      height: (!keepDimensions && dims.height) || current.height || null,
      // a new coordinate size invalidates the viewBox of a hall plan version (maps fall back to "0 0 w h")
      viewBox: newSize ? null : current.viewBox || null,
    });
    if (previousPath && previousPath !== stored.s3Path && !(await isVersionFile(previousPath))) {
      try {
        await removeBoothAsset(previousPath);
      } catch (error) {
        // the new image is saved; an orphaned old file is harmless
        captureError(error, {
          feature: FEATURE,
          operation: 'remove-previous-booth-plan-image',
          consoleMessage: 'Error removing the previous hall plan image',
          context: { eventId, s3Path: previousPath },
        });
      }
    }
    return { plan: saved, dims };
  }, [eventId, isVersionFile, readCurrentPlan, readPlanFileDimensions, saveBoothPlan]);

  /**
   * Sets Event.boothPlan to null, then deletes the plan file. Database first (like uploadPlanImage):
   * a failed update must not leave the event pointing at a deleted file.
   */
  const removePlanImage = useCallback(async () => {
    const current = await readCurrentPlan();
    const s3Path = current.image?.s3Path || null;
    await saveBoothPlan(null);
    if (current.activeVersionId) {
      // no plan any more: the map shows no version; mirror that in the version status
      await archiveActiveVersions(null);
      await loadVersions();
    }
    if (s3Path && !(await isVersionFile(s3Path))) {
      try {
        await removeBoothAsset(s3Path);
      } catch (error) {
        // the plan reference is already removed; an orphaned file is harmless
        captureError(error, {
          feature: FEATURE,
          operation: 'remove-booth-plan-image',
          consoleMessage: 'Error removing the hall plan image file',
          context: { eventId, s3Path },
        });
      }
    }
  }, [archiveActiveVersions, eventId, isVersionFile, loadVersions, readCurrentPlan, saveBoothPlan]);

  const savePlanDimensions = useCallback(async (width, height) => {
    const current = await readCurrentPlan();
    const nextWidth = numOrNull(width);
    const nextHeight = numOrNull(height);
    const unchanged = nextWidth === numOrNull(current.width) && nextHeight === numOrNull(current.height);
    // a changed coordinate size invalidates the viewBox of a hall plan version (maps fall back to "0 0 w h")
    return saveBoothPlan({ ...current, width: nextWidth, height: nextHeight, viewBox: unchanged ? current.viewBox || null : null });
  }, [readCurrentPlan, saveBoothPlan]);

  /** `extra(current)` may return fields derived from the freshly read plan (e.g. a first coordinate space). */
  const touchAreasUpdatedAt = useCallback(async (extra) => {
    const current = await readCurrentPlan();
    const additions = typeof extra === 'function' ? extra(current) : (extra || {});
    return saveBoothPlan({ ...current, ...additions, areasUpdatedAt: new Date().toISOString() });
  }, [readCurrentPlan, saveBoothPlan]);

  // ----- Areas ---------------------------------------------------------------

  /**
   * Writes parsed import areas. replace=true deletes all existing areas first; otherwise
   * areas with an existing booth number are updated and the rest created.
   * Saves width/height of the import when the event has no coordinate space yet.
   * The existing areas are re-read from the server first (not the cached state), so rows of an
   * earlier, partly failed import are deleted/updated too instead of being duplicated.
   */
  const writeImportedAreas = useCallback(async ({ width, height, importAreasList, replace, onProgress }) => {
    const errorsOut = [];
    let deleted = 0;
    // legacy rows only: the rows of hall plan versions belong to their version
    const existing = (await queryAllPages(BOOTH_PLAN_AREAS_BY_EVENT, { eventId }, 'boothPlanAreasByEvent'))
      .filter((area) => !area.planVersionId);

    if (replace && existing.length > 0) {
      const failures = await runInChunks(existing, (area) => client.graphql({
        query: DELETE_BOOTH_PLAN_AREA,
        variables: { input: { id: area.id } },
      }));
      deleted = existing.length - failures.length;
      failures.forEach(({ item, error }) => errorsOut.push(`${item.boothNumber}: ${errorMessage(error)}`));
      if (failures.length) {
        captureError(failures[0].error, {
          feature: FEATURE,
          operation: 'import-areas-delete-existing',
          consoleMessage: 'Error deleting existing hall plan areas',
          context: { eventId, failed: failures.length },
        });
      }
    }

    const existingByKey = new Map(replace ? [] : existing.map((a) => [normalizeBoothNumber(a.boothNumber), a]));
    let done = 0;
    let created = 0;
    let updated = 0;
    const total = importAreasList.length;
    const failures = await runInChunks(importAreasList, async (area) => {
      const match = existingByKey.get(normalizeBoothNumber(area.boothNumber));
      const fields = {
        boothNumber: area.boothNumber,
        points: area.points,
        labelX: numOrNull(area.labelX),
        labelY: numOrNull(area.labelY),
        source: 'import',
      };
      // variant fields only when the JSON carries them (exports of older versions must not send the new keys)
      if ('variant' in area) fields.variant = typeof area.variant === 'string' && area.variant.trim() ? area.variant.trim() : null;
      if ('size' in area) fields.size = numOrNull(area.size);
      if ('bookable' in area) fields.bookable = area.bookable === false ? false : null;
      // features only when the JSON carries them (same rule as above)
      if ('positionType' in area) fields.positionType = cleanPositionType(area.positionType);
      if ('zone' in area) fields.zone = cleanZone(area.zone);
      if (match) {
        await client.graphql({ query: UPDATE_BOOTH_PLAN_AREA, variables: { input: { id: match.id, ...fields } } });
        updated += 1;
      } else {
        await client.graphql({ query: CREATE_BOOTH_PLAN_AREA, variables: { input: { eventId, ...fields } } });
        created += 1;
      }
      done += 1;
      if (typeof onProgress === 'function') onProgress(done, total);
    });
    failures.forEach(({ item, error }) => errorsOut.push(`${item.boothNumber}: ${errorMessage(error)}`));
    if (failures.length) {
      captureError(failures[0].error, {
        feature: FEATURE,
        operation: 'import-areas-write',
        consoleMessage: 'Error writing hall plan areas',
        context: { eventId, failed: failures.length },
      });
    }

    // remember m² / width / depth of the import for "Create booths from unassigned areas"
    setAreaMeta((prev) => {
      const next = { ...prev };
      importAreasList.forEach((area) => {
        next[normalizeBoothNumber(area.boothNumber)] = {
          area: numOrNull(area.area),
          width: numOrNull(area.width),
          depth: numOrNull(area.depth),
        };
      });
      return next;
    });

    // the legacy rows become live: no active version, and no version viewBox (maps use "0 0 width height")
    let hadActiveVersion = false;
    await touchAreasUpdatedAt((current) => {
      hadActiveVersion = Boolean(current.activeVersionId);
      return {
        ...(!current.width && !current.height && width && height ? { width, height } : {}),
        activeVersionId: null,
        viewBox: null,
      };
    });
    if (hadActiveVersion) await archiveActiveVersions(null);
    return { created, updated, deleted, errors: errorsOut };
  }, [archiveActiveVersions, eventId, touchAreasUpdatedAt]);

  const importAreas = useCallback(async ({ width, height, areas: importAreasList }, { replace = true, onProgress } = {}) => {
    // '#…' is reserved for placeholder areas of unnamed shapes: such rows are reported, never imported
    // (the import modal already drops them; a list of only such rows must not delete the existing areas)
    const reserved = (importAreasList || []).filter((area) => isUnnamedBoothNumber(area.boothNumber));
    const allowed = (importAreasList || []).filter((area) => !isUnnamedBoothNumber(area.boothNumber));
    if (reserved.length > 0 && allowed.length === 0) {
      throw new Error('Booth numbers must not start with "#" (reserved for unnamed shapes).');
    }
    try {
      const outcome = await writeImportedAreas({ width, height, importAreasList: allowed, replace, onProgress });
      return {
        ...outcome,
        errors: [
          ...reserved.map((area) => `${area.boothNumber}: booth numbers must not start with "#" (reserved for unnamed shapes) - skipped`),
          ...outcome.errors,
        ],
      };
    } finally {
      // also after a failure: the cached list must show what was actually written
      await Promise.all([loadAreas(), loadMap({ silent: true }), loadVersions()]);
    }
  }, [loadAreas, loadMap, loadVersions, writeImportedAreas]);

  const createArea = useCallback(async ({ boothNumber, points }) => {
    // '#…' is reserved for placeholder areas of unnamed shapes (AddAreaModal validates this first)
    if (isUnnamedBoothNumber(boothNumber)) {
      throw new Error('Booth numbers must not start with "#" (reserved for unnamed shapes).');
    }
    const centroid = polygonCentroid(points);
    const current = await readCurrentPlan();
    await client.graphql({
      query: CREATE_BOOTH_PLAN_AREA,
      variables: {
        input: {
          eventId,
          boothNumber: boothNumber.trim(),
          points,
          labelX: Math.round(centroid.x * 10) / 10,
          labelY: Math.round(centroid.y * 10) / 10,
          source: 'manual',
          // belongs to the live area set: the active version, or the legacy rows
          planVersionId: current.activeVersionId || null,
        },
      },
    });
    await touchAreasUpdatedAt();
    await Promise.all([loadAreas(), loadMap({ silent: true })]);
  }, [eventId, loadAreas, loadMap, readCurrentPlan, touchAreasUpdatedAt]);

  const deleteArea = useCallback(async (areaId) => {
    await client.graphql({ query: DELETE_BOOTH_PLAN_AREA, variables: { input: { id: areaId } } });
    setAllAreas((prev) => prev.filter((a) => a.id !== areaId));
    await touchAreasUpdatedAt();
    await loadMap({ silent: true });
  }, [loadMap, touchAreasUpdatedAt]);

  /** true when the area belongs to the live area set (active version, or legacy rows without one). */
  const isLiveArea = useCallback((area) => {
    const live = activeVersionIdRef.current;
    return live ? area?.planVersionId === live : !area?.planVersionId;
  }, []);

  /**
   * Writes plan fields of BoothPlanArea rows: updates [{ id, variant?, size?, bookable?, positionType?, zone?,
   * openSides?, attributes? }] (empty values are stored as null; attributes as AWSJSON). Only the given keys
   * are written; chunks of 10. live=true touches Event.boothPlan.areasUpdatedAt (the booking map shows these
   * areas). Returns { updated, errors }.
   */
  const writeAreaVariants = useCallback(async (updates, { live = false } = {}) => {
    const list = (updates || []).filter((u) => u && u.id);
    const done = [];
    const failures = await runInChunks(list, async (update) => {
      const input = { id: update.id };
      if ('variant' in update) input.variant = cleanVariantCode(update.variant);
      if ('size' in update) input.size = sizeOrNull(update.size);
      if ('bookable' in update) input.bookable = update.bookable === false ? false : null;
      if ('positionType' in update) input.positionType = cleanPositionType(update.positionType);
      if ('zone' in update) input.zone = cleanZone(update.zone);
      if ('openSides' in update) input.openSides = openSidesOrNull(update.openSides);
      if ('attributes' in update) {
        const attributes = attributesOrNull(update.attributes);
        input.attributes = attributes ? JSON.stringify(attributes) : null;
      }
      const response = await client.graphql({ query: UPDATE_BOOTH_PLAN_AREA, variables: { input } });
      done.push(response.data.updateBoothPlanArea || { ...update, ...input });
    });
    if (done.length > 0) {
      const byId = new Map(done.map((area) => [area.id, area]));
      setAllAreas((prev) => prev.map((area) => (byId.has(area.id) ? { ...area, ...byId.get(area.id) } : area)));
    }
    const errorsOut = failures.map(({ item, error }) => `${item.boothNumber || item.id}: ${errorMessage(error)}`);
    if (failures.length) {
      captureError(failures[0].error, {
        feature: FEATURE,
        operation: 'write-area-variants',
        consoleMessage: 'Error writing the plan code / features of hall plan areas',
        context: { eventId, failed: failures.length, total: list.length },
      });
    }
    if (live && done.length > 0) {
      await touchAreasUpdatedAt();
      await loadMap({ silent: true });
    }
    return { updated: done.length, errors: errorsOut };
  }, [eventId, loadMap, touchAreasUpdatedAt]);

  /** Sets the plan code of one area ('' removes it, stored as null). Throws on an invalid code or a failed write. */
  const updateAreaVariant = useCallback(async (area, value) => {
    const variant = cleanVariantCode(value);
    if (variant && !isValidVariantCode(variant)) {
      throw new Error(`"${variant}" is not a valid plan code (letters, digits, ".", "_" and "-", max. 16 characters).`);
    }
    const outcome = await writeAreaVariants([{ id: area.id, boothNumber: area.boothNumber, variant }], { live: isLiveArea(area) });
    if (outcome.errors.length > 0) throw new Error(outcome.errors.join('; '));
    return outcome;
  }, [isLiveArea, writeAreaVariants]);

  /**
   * Sets features of one area: { positionType?, zone? } ('' removes a value, stored as null; a standard
   * position key is stored in its standard spelling). Throws on a failed write.
   */
  const updateAreaFeatures = useCallback(async (area, features) => {
    const update = { id: area.id, boothNumber: area.boothNumber };
    if (features && 'positionType' in features) update.positionType = cleanPositionType(features.positionType);
    if (features && 'zone' in features) update.zone = cleanZone(features.zone);
    const outcome = await writeAreaVariants([update], { live: isLiveArea(area) });
    if (outcome.errors.length > 0) throw new Error(outcome.errors.join('; '));
    return outcome;
  }, [isLiveArea, writeAreaVariants]);

  /**
   * BoothPlanArea.bookable: false takes the booth off sale, true stores null (= bookable).
   * `live` overrides the live check (booths of the map always belong to the live area set).
   */
  const updateAreaBookable = useCallback(async (area, bookable, { live = null } = {}) => {
    if (!area?.id) throw new Error('This booth has no area on the hall plan.');
    const outcome = await writeAreaVariants(
      [{ id: area.id, boothNumber: area.boothNumber, bookable: Boolean(bookable) }],
      { live: live ?? isLiveArea(area) },
    );
    if (outcome.errors.length > 0) throw new Error(outcome.errors.join('; '));
    return outcome;
  }, [isLiveArea, writeAreaVariants]);

  /** Export file (same format as the JSON import), incl. m² / width / depth remembered from an import. */
  const buildAreasExport = useCallback(() => {
    const plan = eventRef.current?.boothPlan || {};
    return areasToExportJson({
      width: plan.width,
      height: plan.height,
      areas: areas.map((area) => ({ ...area, ...(areaMeta[normalizeBoothNumber(area.boothNumber)] || {}) })),
    });
  }, [areas, areaMeta]);

  // ----- Hall plan versions (SVG import) -------------------------------------

  /** BoothPlanArea rows of one hall plan version, sorted by booth number. Throws. */
  const loadVersionAreas = useCallback(async (planVersionId) => {
    const items = await queryAllPages(BOOTH_PLAN_AREAS_BY_PLAN_VERSION, { planVersionId }, 'boothPlanAreasByPlanVersion');
    return sortAreas(items);
  }, []);

  /**
   * Reads and checks an uploaded hall plan SVG (nothing is saved): parseHallPlan + matchWithSystem
   * against the booths of this event (fresh booth map) and, as diff base, the areas of the active version.
   *
   * @returns {Promise<{fileName, fileSize, plan, match, issues, counts, systemStands, previousVersion, hasPrevious}>}
   *   issues = parser + match issues, sorted (errors first); match is null when the SVG could not be read at all.
   */
  const analyzeHallPlanFile = useCallback(async (file) => {
    const text = await readFileText(file);
    const plan = parseHallPlan(text);
    const [rawMap, current] = await Promise.all([fetchEventBoothMap({ eventId }), readCurrentPlan()]);
    const map = hydrateBoothMap(rawMap);
    setBoothMap(map || null);
    // physical booths of the event plus one entry per variant product (matched by variant code / alias)
    const systemStands = systemStandsFromMap(map || { booths: [] });

    let previous = null;
    let previousVersion = null;
    if (current.activeVersionId) {
      const [list, previousAreas] = await Promise.all([listVersions(), loadVersionAreas(current.activeVersionId)]);
      previousVersion = list.find((v) => v.id === current.activeVersionId) || null;
      const previousViewBox = previousVersion?.viewBox || current.viewBox;
      if (previousAreas.length > 0 && previousViewBox) previous = planFromAreas(previousAreas, previousViewBox);
    }

    const hasGeometry = Array.isArray(plan.viewBox) && plan.viewBox.length === 4;
    const match = hasGeometry ? matchWithSystem(plan, systemStands, previous, 0.15) : null;
    const issues = sortIssues([...(plan.issues || []), ...((match && match.issues) || [])]);
    return {
      fileName: file.name,
      fileSize: file.size,
      plan,
      match,
      issues,
      counts: issueCounts(issues),
      systemStands,
      previousVersion,
      hasPrevious: Boolean(previous),
    };
  }, [eventId, listVersions, loadVersionAreas, readCurrentPlan]);

  /**
   * Makes a version live: Event.boothPlan gets its background SVG, size, viewBox and activeVersionId
   * (the other boothPlan keys are kept); the previously active version is archived.
   * Refused when the version has blocking errors (versionActivationState: every error except
   * 'missing-id') or its areas were not saved completely. Shapes without a booth number do not block:
   * they are stored as placeholder areas ('#<n>') that stay unbookable on the map.
   *
   * savedAreaCount: number of areas the caller has just written successfully (saveHallPlanVersion). It
   * replaces the area query, because the byPlanVersion GSI may not return rows written moments ago.
   * Without it the areas are queried (with short retries): a version that was never live needs all
   * its areas (summary.areas, else summary.withId + summary.unnamed placeholders; versions saved before
   * the placeholders existed have no summary.unnamed and only named areas); a version that was live
   * before (activatedAt) may have been edited in the areas list since, so it only needs at least one area.
   */
  const activateVersionInternal = useCallback(async (version, { savedAreaCount = null } = {}) => {
    const activation = versionActivationState(version);
    if (!activation.canActivate) {
      throw new Error(`This hall plan version has errors and cannot be activated (${blockerCodes(activation.blockers)}).`);
    }
    if (!version.backgroundFile?.s3Path || !version.viewBox) {
      throw new Error('This hall plan version has no background file or viewBox and cannot be activated.');
    }
    if (savedAreaCount !== null) {
      if (savedAreaCount <= 0) throw new Error('No areas of this version were saved; it cannot be activated.');
    } else {
      const summary = version.summary || {};
      const withId = numOrNull(summary.withId);
      const expected = numOrNull(summary.areas)
        ?? (withId !== null ? withId + (numOrNull(summary.unnamed) ?? 0) : null);
      const required = version.activatedAt ? 1 : Math.max(1, expected ?? 1);
      let found = (await loadVersionAreas(version.id)).length;
      for (let attempt = 0; found < required && attempt < AREA_CHECK_RETRIES; attempt += 1) {
        await delay(AREA_CHECK_DELAY_MS);
        found = (await loadVersionAreas(version.id)).length;
      }
      if (found < required) {
        throw new Error(found === 0
          ? 'No areas of this version were found; it cannot be activated. Please try again in a moment.'
          : `${found} of ${required} areas of this version were found; it was not activated. Please try again in a moment. If the import was incomplete (see the notes of the version), delete the version and import the SVG again.`);
      }
    }
    const box = parseViewBoxString(version.viewBox);
    const now = new Date().toISOString();
    const current = await readCurrentPlan();
    // Event first: activeVersionId is what the booking map reads; the statuses only mirror it
    await saveBoothPlan({
      ...current,
      image: version.backgroundFile,
      width: numOrNull(version.width) ?? (box ? box[2] : null),
      height: numOrNull(version.height) ?? (box ? box[3] : null),
      viewBox: version.viewBox,
      activeVersionId: version.id,
      areasUpdatedAt: now,
    });
    await archiveActiveVersions(version.id);
    await client.graphql({
      query: UPDATE_HALL_PLAN_VERSION,
      variables: { input: { id: version.id, status: 'active', activatedAt: now } },
    });
  }, [archiveActiveVersions, loadVersionAreas, readCurrentPlan, saveBoothPlan]);

  /** true when another hall plan version than `versionId` is live (Event.boothPlan.activeVersionId); false when unreadable. */
  const hasOtherLiveVersion = useCallback(async (versionId) => {
    try {
      const current = await readCurrentPlan();
      return Boolean(current.activeVersionId) && current.activeVersionId !== versionId;
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'check-live-hall-plan-version',
        consoleMessage: 'Error reading the live hall plan version before an activation',
        context: { eventId, planVersionId: versionId },
      });
      return false;
    }
  }, [eventId, readCurrentPlan]);

  /**
   * Activates a version. carryOver: the selected corrections of the carry-over dialog (previewCarryOver);
   * they are written onto the areas of the version FIRST - when one of them fails, nothing is activated
   * (the dialog can be repeated). savedAreaCount: see activateVersionInternal (a version saved moments ago).
   */
  const activateVersion = useCallback(async (version, { carryOver = [], savedAreaCount = null } = {}) => {
    try {
      const updates = carryOverUpdates(carryOver);
      if (updates.length > 0) {
        const outcome = await writeAreaVariants(updates, { live: false });
        if (outcome.errors.length > 0) {
          throw new Error(`${outcome.errors.length} of ${updates.length} booth corrections could not be taken over; the version was not activated (${outcome.errors.slice(0, 3).join('; ')}).`);
        }
      }
      await activateVersionInternal(version, { savedAreaCount });
    } finally {
      await Promise.all([loadVersions(), loadAreas(), loadMap({ silent: true })]);
    }
  }, [activateVersionInternal, loadAreas, loadMap, loadVersions, writeAreaVariants]);

  /**
   * Carry-over dialog data before `version` becomes live (decision 14.10.2026 "Dialog mit Auswahl"): the
   * manual corrections of the live version (areaCarryOverCorrections) that the version would lose. To detect
   * hand-set plan codes the live version's source SVG is read (best effort: unreadable = only codes missing in
   * the new version are offered). versionAreas: the areas of `version` when the caller has them (fresh save).
   *
   * @returns {Promise<{liveVersion: object|null, corrections: object[]}>} no live version (or `version` is live) = []
   */
  const previewCarryOver = useCallback(async (version, { versionAreas = null } = {}) => {
    const current = await readCurrentPlan();
    const liveId = current.activeVersionId || null;
    if (!liveId || !version || liveId === version.id) return { liveVersion: null, corrections: [] };
    const [list, liveAreas, targetAreas] = await Promise.all([
      listVersions(),
      loadVersionAreas(liveId),
      Array.isArray(versionAreas) ? Promise.resolve(versionAreas) : loadVersionAreas(version.id),
    ]);
    const liveVersion = list.find((v) => v.id === liveId) || { id: liveId };
    let svgVariantByKey = null;
    if (liveVersion.sourceFile?.s3Path) {
      try {
        const plan = parseHallPlan(await readBoothAssetText(liveVersion.sourceFile.s3Path), { sanitize: false });
        if (Array.isArray(plan.stands) && plan.stands.length > 0) {
          svgVariantByKey = new Map();
          plan.stands.forEach((stand) => {
            const key = normalizeBoothNumber(stand && stand.id);
            if (key && !svgVariantByKey.has(key)) svgVariantByKey.set(key, cleanVariantCode(stand.variant));
          });
        }
      } catch (error) {
        captureError(error, {
          feature: FEATURE,
          operation: 'read-live-version-svg-for-carry-over',
          consoleMessage: 'Error reading the SVG of the live hall plan version (carry-over of corrections)',
          context: { eventId, liveVersionId: liveId, planVersionId: version.id },
        });
        svgVariantByKey = null;
      }
    }
    return {
      liveVersion,
      corrections: areaCarryOverCorrections(liveAreas, targetAreas, { svgVariantByKey }),
    };
  }, [eventId, listVersions, loadVersionAreas, readCurrentPlan]);

  /**
   * Takes the live version offline: Event.boothPlan loses activeVersionId, viewBox and (when it is this
   * version's background SVG) the plan image, so the exhibitor map shows no plan until another version is
   * activated. Areas and files of the version are kept; its status becomes 'archived'. Returns false when
   * the version is not the live one (nothing written).
   */
  const deactivateVersionInternal = useCallback(async (version, currentPlan = null) => {
    const current = currentPlan || await readCurrentPlan();
    if (!current.activeVersionId || current.activeVersionId !== version.id) return false;
    const imageIsVersionBackground = Boolean(current.image?.s3Path)
      && current.image.s3Path === version.backgroundFile?.s3Path;
    await saveBoothPlan({
      ...current,
      image: imageIsVersionBackground ? null : current.image,
      viewBox: null,
      activeVersionId: null,
      areasUpdatedAt: new Date().toISOString(),
    });
    await archiveActiveVersions(null);
    return true;
  }, [archiveActiveVersions, readCurrentPlan, saveBoothPlan]);

  const deactivateVersion = useCallback(async (version) => {
    try {
      const done = await deactivateVersionInternal(version);
      if (!done) throw new Error('This hall plan version is not the active one.');
    } finally {
      await Promise.all([loadVersions(), loadAreas(), loadMap({ silent: true })]);
    }
  }, [deactivateVersionInternal, loadAreas, loadMap, loadVersions]);

  /**
   * Saves an analysed SVG as a new version: uploads the sanitized source and the background SVG,
   * creates the HallPlanVersion (status 'draft') and its BoothPlanArea rows (chunks of 10; unnamed shapes
   * as placeholder areas '#<n>'), then activates it when asked, the version has no blocking errors
   * (canActivatePlan: 'missing-id' does not block; the tab asks for confirmation first) and all areas
   * were written. The summary is stored with `unnamed` (missing-id count) and `areas` (area rows expected).
   * onProgress({ phase: 'upload', percent }) / ({ phase: 'areas', done, total }).
   *
   * When another version is live, "activate" does NOT activate: the result carries pendingActivation
   * { version, savedAreaCount, areas } and the tab shows the carry-over dialog (previewCarryOver) first,
   * then calls activateVersion(version, { carryOver, savedAreaCount }).
   *
   * @returns {Promise<{version, created: number, total: number, errors: string[], activated: boolean,
   *   pendingActivation: {version, savedAreaCount: number, areas: object[]}|null}>}
   */
  const saveHallPlanVersion = useCallback(async ({ analysis, uploadedBy, activate = false, onProgress }) => {
    const { plan, match, issues, counts } = analysis || {};
    const report = (progress) => {
      if (typeof onProgress === 'function') onProgress(progress);
    };
    if (!plan || !Array.isArray(plan.viewBox) || !plan.backgroundSvg) {
      throw new Error('The SVG could not be read; there is nothing to save.');
    }
    if (!plan.sourceSvg) throw new Error('The sanitized source SVG is missing; the file was not saved.');

    // every stand becomes an area: named stands with their number, unnamed shapes as placeholders ('#<n>')
    const areaCount = standsToAreaInputs(plan, { eventId, includeUnnamed: true }).length;
    const issueList = Array.isArray(issues) ? issues : [];
    const blockers = activationBlockers(issueList);
    // blockers first: when the stored list has to be shortened, the issues that decide the activation stay
    const storedIssues = [...blockers, ...issueList.filter((issue) => !blockers.includes(issue))];
    const keyPrefix = `booth-plans/${eventId}/versions`;
    const base = baseFileName(analysis.fileName);
    const uploaded = [];
    let version = null;
    try {
      report({ phase: 'upload', percent: 0 });
      const sourceFile = await uploadBoothAsset(svgFile(plan.sourceSvg, `${base}-source.svg`), {
        keyPrefix,
        contentType: SVG_CONTENT_TYPE,
        onProgress: (percent) => report({ phase: 'upload', percent: Math.round(percent / 2) }),
      });
      uploaded.push(sourceFile.s3Path);
      const backgroundFile = await uploadBoothAsset(svgFile(plan.backgroundSvg, `${base}-background.svg`), {
        keyPrefix,
        contentType: SVG_CONTENT_TYPE,
        onProgress: (percent) => report({ phase: 'upload', percent: 50 + Math.round(percent / 2) }),
      });
      uploaded.push(backgroundFile.s3Path);

      const [, , vbWidth, vbHeight] = plan.viewBox;
      const response = await client.graphql({
        query: CREATE_HALL_PLAN_VERSION,
        variables: {
          input: {
            eventId,
            status: 'draft',
            fileName: analysis.fileName || null,
            viewBox: viewBoxToString(plan.viewBox),
            width: numOrNull(vbWidth),
            height: numOrNull(vbHeight),
            sourceFile: fileInputOrNull(sourceFile),
            backgroundFile: fileInputOrNull(backgroundFile),
            summary: JSON.stringify({ ...(plan.summary || {}), unnamed: unnamedCount(issueList), areas: areaCount }),
            issues: issuesForStorage(storedIssues),
            matchSummary: JSON.stringify(match ? {
              matched: match.matched,
              onlyInPlan: match.onlyInPlan,
              onlyInSystem: match.onlyInSystem,
              unitsPerMeter: match.unitsPerMeter,
              diff: match.diff,
              matchedVariants: match.matchedVariants || [],
              variantsWithoutProduct: match.variantsWithoutProduct || [],
              previousVersionId: analysis.previousVersion?.id || null,
              previousFileName: analysis.previousVersion?.fileName || null,
              compared: Boolean(analysis.hasPrevious),
            } : null),
            standsCount: numOrNull(plan.summary?.stands) ?? (plan.stands || []).length,
            errorCount: counts?.errors || 0,
            warningCount: counts?.warnings || 0,
            unitsPerMeter: match ? numOrNull(match.unitsPerMeter) : null,
            uploadedBy: uploadedBy || null,
          },
        },
      });
      version = normalizeVersion(response.data.createHallPlanVersion);
      if (!version) throw new Error('The hall plan version was not created.');
    } catch (error) {
      // nothing references the uploaded files yet: remove them again
      await Promise.all(uploaded.map((s3Path) => removeBoothAsset(s3Path).catch((removeError) => {
        captureError(removeError, {
          feature: FEATURE,
          operation: 'cleanup-hall-plan-version-files',
          consoleMessage: 'Error removing the files of an unsaved hall plan version',
          context: { eventId, s3Path },
        });
      })));
      throw error;
    }

    const errorsOut = [];
    let activated = false;
    let activationAttempted = false;
    let pendingActivation = null;
    let total = 0;
    let created = 0;
    // the written area rows (the carry-over dialog needs their ids; the byPlanVersion GSI may lag behind)
    const createdAreas = [];
    try {
      const inputs = standsToAreaInputs(plan, { eventId, planVersionId: version.id, includeUnnamed: true });
      total = inputs.length;
      let done = 0;
      report({ phase: 'areas', done, total });
      const failures = await runInChunks(inputs, async (input) => {
        const response = await client.graphql({ query: CREATE_BOOTH_PLAN_AREA, variables: { input } });
        createdAreas.push(response.data.createBoothPlanArea || { ...input });
        done += 1;
        report({ phase: 'areas', done, total });
      });
      failures.forEach(({ item, error }) => errorsOut.push(`${item.boothNumber}: ${errorMessage(error)}`));
      if (failures.length) {
        captureError(failures[0].error, {
          feature: FEATURE,
          operation: 'create-hall-plan-version-areas',
          consoleMessage: 'Error writing the areas of a hall plan version',
          context: { eventId, planVersionId: version.id, failed: failures.length, total },
        });
        try {
          await client.graphql({
            query: UPDATE_HALL_PLAN_VERSION,
            variables: { input: { id: version.id, notes: `Incomplete: ${failures.length} of ${total} areas could not be saved.` } },
          });
        } catch (error) {
          captureError(error, {
            feature: FEATURE,
            operation: 'note-incomplete-hall-plan-version',
            consoleMessage: 'Error marking a hall plan version as incomplete',
            context: { eventId, planVersionId: version.id },
          });
        }
      }

      if (activate) {
        if (!canActivatePlan(issueList)) {
          errorsOut.push(`The version has errors that block the activation (${blockerCodes(blockers)}) and was saved as a draft only.`);
        } else if (failures.length) {
          errorsOut.push('Not all areas were saved: the version was not activated.');
        } else if (await hasOtherLiveVersion(version.id)) {
          // another version is live: the tab asks which corrections to take over first (carry-over dialog),
          // then activates via activateVersion(version, { carryOver, savedAreaCount })
          pendingActivation = { version, savedAreaCount: total - failures.length, areas: createdAreas };
        } else {
          // the version and its areas exist from here on: an activation failure must not throw, otherwise
          // the report keeps its save buttons and a retry would create a second version with duplicate areas
          activationAttempted = true;
          try {
            await activateVersionInternal(version, { savedAreaCount: total - failures.length });
            activated = true;
          } catch (error) {
            captureError(error, {
              feature: FEATURE,
              operation: 'activate-saved-hall-plan-version',
              consoleMessage: 'Error activating a newly saved hall plan version',
              context: { eventId, planVersionId: version.id },
            });
            errorsOut.push(`The version was saved but could not be activated (${errorMessage(error)}). Check the versions list and activate it there.`);
          }
        }
      }
      created = total - failures.length;
      return {
        version, created, total, errors: errorsOut, activated, pendingActivation,
      };
    } catch (error) {
      // the version row exists: report instead of throwing, so the report switches to its result view
      captureError(error, {
        feature: FEATURE,
        operation: 'save-hall-plan-version-areas',
        consoleMessage: 'Error saving the areas of a hall plan version',
        context: { eventId, planVersionId: version.id },
      });
      errorsOut.push(`The version was created but its areas could not be saved completely (${errorMessage(error)}). Delete the version and import the SVG again.`);
      return {
        version, created, total, errors: errorsOut, activated, pendingActivation: null,
      };
    } finally {
      // map also after a failed activation: the event may already point to the new version
      await Promise.all([loadVersions(), loadAreas(), ...(activationAttempted ? [loadMap({ silent: true })] : [])]);
    }
  }, [activateVersionInternal, eventId, hasOtherLiveVersion, loadAreas, loadMap, loadVersions]);

  /**
   * Deletes a version: its areas (chunks), its files (except a file that is still the event's plan image)
   * and the version row. The live version (Event.boothPlan.activeVersionId) is taken offline first
   * (deactivateVersionInternal), so its background SVG is no longer the plan image and is deleted too.
   * The areas go first; when some cannot be deleted the version is kept so the deletion can be repeated.
   */
  const deleteVersion = useCallback(async (version) => {
    let current = await readCurrentPlan();
    const wasLive = current.activeVersionId === version.id;
    if (wasLive) {
      await deactivateVersionInternal(version, current);
      current = await readCurrentPlan();
    }
    const versionAreas = await loadVersionAreas(version.id);
    const failures = await runInChunks(versionAreas, (area) => client.graphql({
      query: DELETE_BOOTH_PLAN_AREA,
      variables: { input: { id: area.id } },
    }));
    if (failures.length) {
      captureError(failures[0].error, {
        feature: FEATURE,
        operation: 'delete-hall-plan-version-areas',
        consoleMessage: 'Error deleting the areas of a hall plan version',
        context: { eventId, planVersionId: version.id, failed: failures.length },
      });
      await loadAreas();
      throw new Error(`${failures.length} of ${versionAreas.length} areas of the version could not be deleted; the version is kept. Please try again.`);
    }

    const planImagePath = current.image?.s3Path || null;
    const files = [version.sourceFile?.s3Path, version.backgroundFile?.s3Path].filter((p) => p && p !== planImagePath);
    await Promise.all(files.map((s3Path) => removeBoothAsset(s3Path).catch((error) => {
      // the version is deleted anyway; an orphaned file is harmless
      captureError(error, {
        feature: FEATURE,
        operation: 'remove-hall-plan-version-file',
        consoleMessage: 'Error removing a file of a hall plan version',
        context: { eventId, planVersionId: version.id, s3Path },
      });
    })));

    await client.graphql({ query: DELETE_HALL_PLAN_VERSION, variables: { input: { id: version.id } } });
    setVersions((prev) => (prev ? prev.filter((v) => v.id !== version.id) : prev));
    setAllAreas((prev) => prev.filter((a) => a.planVersionId !== version.id));
    if (wasLive) await Promise.all([loadVersions(), loadMap({ silent: true })]);
  }, [deactivateVersionInternal, eventId, loadAreas, loadMap, loadVersionAreas, loadVersions, readCurrentPlan]);

  /**
   * "Re-read from the SVG" of a hall plan version (nothing is written): reads the stored source SVG
   * (parseHallPlan without sanitizing) and pairs every named area of the version with the stand of the same
   * number. Returns { rows: [{ area, svg: { variant, size, positionType?, zone?, openSides?, attributes? } | null }],
   * variants: distinct SVG codes, standsWithVariant, standsWithFeatures, missingInSvg: area numbers without a stand }.
   */
  const previewVersionVariants = useCallback(async (version) => {
    const s3Path = version?.sourceFile?.s3Path;
    if (!s3Path) throw new Error('This hall plan version has no stored source SVG.');
    const [text, versionAreas] = await Promise.all([readBoothAssetText(s3Path), loadVersionAreas(version.id)]);
    const plan = parseHallPlan(text, { sanitize: false });
    if (!plan.stands || plan.stands.length === 0) {
      // A parse failure returns no stands: report its error instead of "no data-variant attributes"
      const firstError = (plan.issues || []).find((issue) => issue && issue.level === 'error');
      throw new Error(firstError?.message || 'The SVG of this version contains no booths.');
    }
    const svgByKey = new Map();
    let standsWithVariant = 0;
    let standsWithFeatures = 0;
    (plan.stands || []).forEach((stand) => {
      const key = normalizeBoothNumber(stand && stand.id);
      if (!key || svgByKey.has(key)) return;
      const variant = cleanVariantCode(stand.variant);
      if (variant) standsWithVariant += 1;
      const svg = { variant, size: sizeOrNull(stand.size) };
      // features only when the parser delivers them (older exports have none: nothing is cleared)
      AREA_FEATURE_FIELDS.forEach(({ field }) => {
        if (stand[field] !== undefined && stand[field] !== null) svg[field] = stand[field];
      });
      if (AREA_FEATURE_FIELDS.some(({ field }) => field in svg)) standsWithFeatures += 1;
      svgByKey.set(key, svg);
    });
    const rows = versionAreas
      .filter((area) => !isUnnamedBoothNumber(area.boothNumber))
      .map((area) => ({ area, svg: svgByKey.get(normalizeBoothNumber(area.boothNumber)) || null }));
    const variants = [...new Set([...svgByKey.values()].map((s) => normalizeVariant(s.variant)).filter(Boolean))];
    return {
      rows,
      variants,
      standsWithVariant,
      standsWithFeatures,
      missingInSvg: rows.filter((row) => !row.svg).map((row) => row.area.boothNumber),
    };
  }, [loadVersionAreas]);

  /**
   * Writes the variant changes of previewVersionVariants / svgVariantChanges ([{ update }]) and stores the
   * number of distinct SVG codes (`variants`: the code list) as summary.variants of the version, like the parser
   * summary; areasUpdatedAt is touched only for the live version.
   */
  const applyVersionVariants = useCallback(async (version, changes, { variants = null } = {}) => {
    const current = await readCurrentPlan();
    const live = current.activeVersionId === version.id;
    const outcome = await writeAreaVariants(
      (changes || []).map((change) => ({ ...change.update, boothNumber: change.area?.boothNumber })),
      { live },
    );
    if (Array.isArray(variants)) {
      try {
        await client.graphql({
          query: UPDATE_HALL_PLAN_VERSION,
          // like the parser summary: number of distinct variant codes
          variables: { input: { id: version.id, summary: JSON.stringify({ ...(version.summary || {}), variants: variants.length }) } },
        });
      } catch (error) {
        captureError(error, {
          feature: FEATURE,
          operation: 'store-hall-plan-version-variants',
          consoleMessage: 'Error storing the variant codes in the hall plan version summary',
          context: { eventId, planVersionId: version.id },
        });
        outcome.errors.push(`Summary of the version: ${errorMessage(error)}`);
      }
    }
    await loadVersions();
    return outcome;
  }, [eventId, loadVersions, readCurrentPlan, writeAreaVariants]);

  // ----- Booths (EventProducts) ---------------------------------------------

  // EventProduct-level fields (price, stock, isAvailable) of every booth of the EventProduct and its variant entry
  const patchBooth = useCallback((eventProductId, patch) => {
    setBoothMap((prev) => (prev
      ? {
        ...prev,
        booths: prev.booths.map((b) => (b.eventProductId === eventProductId ? { ...b, ...patch } : b)),
        ...(Array.isArray(prev.variants) ? {
          variants: prev.variants.map((v) => (v.eventProductId === eventProductId ? { ...v, ...patch } : v)),
        } : {}),
      }
      : prev));
  }, []);

  const updateBoothEventProduct = useCallback(async (eventProductId, updates) => {
    const response = await client.graphql({
      query: UPDATE_EVENT_PRODUCT,
      variables: { input: { id: eventProductId, ...updates } },
    });
    const saved = response.data.updateEventProduct || {};
    const patch = {};
    if ('price' in updates && saved.price !== null && saved.price !== undefined) patch.price = saved.price;
    if ('stock' in updates) patch.stock = saved.stock;
    if ('isAvailable' in updates) patch.isAvailable = saved.isAvailable;
    patchBooth(eventProductId, patch);
    setEventBoothProducts((prev) => (prev
      ? prev.map((ep) => (ep.id === eventProductId ? { ...ep, ...patch } : ep))
      : prev));
    // stock / availability change the status, the base price the booth prices (per m², surcharges, time tiers
    // of the price rule: the patch above is only the immediate feedback): reload in the background
    if ('stock' in updates || 'isAvailable' in updates || 'price' in updates) loadMap({ silent: true });
    return saved;
  }, [loadMap, patchBooth]);

  const removeBoothFromEvent = useCallback(async (eventProductId) => {
    await client.graphql({ query: DELETE_EVENT_PRODUCT, variables: { input: { id: eventProductId } } });
    setEventBoothProducts((prev) => (prev ? prev.filter((ep) => ep.id !== eventProductId) : prev));
    await reloadBooths();
  }, [reloadBooths]);

  /**
   * Sets Product.boothNumber (trimmed; '' removes it). The number is the key of the product on every
   * hall plan, so it applies to all events that use the product. Refused when another booth product
   * of this event already has the same normalized number, unless allowDuplicate is set (the tab asks first).
   * Afterwards the booth map and the booth EventProducts are reloaded (and once more after a short delay,
   * because the reads are eventually consistent).
   *
   * @returns {Promise<{id: string, boothNumber: string|null}>}
   */
  const updateProductBoothNumber = useCallback(async (productId, boothNumber, { allowDuplicate = false } = {}) => {
    if (!productId) throw new Error('No product given.');
    const value = cleanBoothNumber(boothNumber);
    if (value && checkBoothNumber(value).error === 'reserved-id') {
      throw new Error('Booth numbers must not start with "#" (reserved for unnamed shapes).');
    }
    const eventProduct = (eventBoothProductsRef.current || []).find((ep) => ep.product?.id === productId);
    if (value && cleanVariantCode(eventProduct?.product?.boothVariant)) {
      // rule "number XOR offer code": an offer with a code stands for all booths with this plan code
      throw new Error(`This product is the offer ${eventProduct.product.boothVariant}; a booth number cannot be set (number XOR offer code).`);
    }
    if (value && !allowDuplicate) {
      const conflicts = findBoothNumberConflicts(eventBoothProductsRef.current, productId, value);
      if (conflicts.length > 0) {
        throw new Error(`Booth number ${value} is already used in this event by ${conflicts.map((ep) => ep.product.sku || ep.product.id).join(', ')}.`);
      }
    }
    const response = await client.graphql({
      query: UPDATE_PRODUCT_BOOTH_NUMBER,
      variables: { input: { id: productId, boothNumber: value } },
    });
    const saved = response.data.updateProduct || { id: productId, boothNumber: value };
    const savedNumber = saved.boothNumber ?? null;
    boothNumberEdits.current.set(productId, { boothNumber: savedNumber, at: Date.now() });
    setEventBoothProducts((prev) => (prev
      ? sortEventBoothProducts(prev.map((ep) => (ep.product?.id === productId
        ? { ...ep, product: { ...ep.product, boothNumber: savedNumber } }
        : ep)))
      : prev));
    // the product list (linking by number, templates) is stale now
    boothProductsRef.current = null;
    setBoothProducts(null);
    await reloadBooths();
    // cancelled when the event changes or the tab unmounts (see reloadBoothsDelayed)
    reloadBoothsDelayed();
    return { id: saved.id || productId, boothNumber: savedNumber };
  }, [reloadBooths, reloadBoothsDelayed]);

  /** All booth products (event_booth + event_space), cached for the session. */
  const loadBoothProducts = useCallback(async ({ force = false } = {}) => {
    if (boothProductsRef.current && !force) return boothProductsRef.current;
    setLoadingKey('products', true);
    setErrorKey('products', null);
    try {
      const lists = await Promise.all(BOOTH_PRODUCT_TYPES.map((type) => queryAllPages(
        PRODUCTS_BY_TYPE,
        { type },
        'productsByType',
        { allowPartial: true },
      )));
      const products = lists.flat().sort(compareBoothProductsForList);
      boothProductsRef.current = products;
      setBoothProducts(products);
      return products;
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'load-booth-products',
        consoleMessage: 'Error loading booth products',
        context: { eventId },
      });
      setErrorKey('products', errorMessage(error));
      return [];
    } finally {
      setLoadingKey('products', false);
    }
  }, [eventId, setErrorKey, setLoadingKey]);

  /**
   * Adds booth products to the event (one EventProduct each). Stock: 1 for a single booth; for a variant
   * product the number of live areas with its code (informational, the booking ignores it). A variant
   * product whose code (or alias) is already used by a product of this event is refused.
   * Returns { created, errors }.
   */
  const addBoothProductsToEvent = useCallback(async (products) => {
    const errorsOut = [];
    let created = 0;
    const offset = (boothMap?.booths?.length || 0) + 1;
    // codes of this event's variant products plus those added in this run
    const usedCodes = new Map();
    (eventBoothProductsRef.current || []).forEach((ep) => variantCodesOf(ep.product).forEach((code) => {
      if (!usedCodes.has(code)) usedCodes.set(code, boothProductLabel(ep.product) || ep.id);
    }));
    for (let i = 0; i < products.length; i += 1) {
      const product = products[i];
      const codes = variantCodesOf(product);
      const conflict = codes.find((code) => usedCodes.has(code));
      if (conflict) {
        errorsOut.push(`${boothProductLabel(product)}: offer code ${conflict} is already used in this event by ${usedCodes.get(conflict)} - skipped`);
        continue;
      }
      // single booth 1; offer with code: its areas (informational); rule offer: not counted here (null)
      let stock = 1;
      if (isVariantProduct(product)) stock = countVariantAreas(areasRef.current, product);
      else if (isRuleOfferProduct(product)) stock = null;
      try {
        await client.graphql({
          query: CREATE_EVENT_PRODUCT,
          variables: {
            input: {
              eventEventProductsId: eventId,
              productEventProductsId: product.id,
              price: numOrNull(product.price),
              stock,
              isAvailable: true,
              sortKey: offset + i,
            },
          },
        });
        created += 1;
        codes.forEach((code) => usedCodes.set(code, boothProductLabel(product)));
      } catch (error) {
        captureError(error, {
          feature: FEATURE,
          operation: 'add-booth-product-to-event',
          consoleMessage: 'Error adding a booth product to the event',
          context: { eventId, productId: product.id },
        });
        errorsOut.push(`${boothProductLabel(product)}: ${errorMessage(error)}`);
      }
    }
    await reloadBooths();
    return { created, errors: errorsOut };
  }, [boothMap, eventId, reloadBooths]);

  // ----- Reservations --------------------------------------------------------

  const searchCompanies = useCallback(async (term) => {
    const response = await client.graphql({
      query: SEARCH_COMPANIES,
      variables: { filter: { name: { matchPhrasePrefix: term } }, limit: 10 },
    });
    return response.data.searchCompanies?.items || [];
  }, []);

  /**
   * Admin option (lock row status 'active', no end date) on ONE booth for a company, via the Lambda field
   * createBoothOption (conditional write: refused when another option or an order claim exists; may replace a hold).
   */
  const createOptionForCompany = useCallback(async ({ booth, companyId }) => {
    const result = await createBoothOption({
      eventId,
      eventProductId: booth.eventProductId,
      companyId,
      boothNumber: booth.boothNumber,
    });
    assertActionResult(result, 'The option could not be created.');
    await loadMap({ silent: true });
    return result;
  }, [eventId, loadMap]);

  /**
   * Releases a lock row (boothlock-<eventId>#<NUMBER>) through the Lambda: a hold via releaseBoothReservation,
   * an admin option via releaseBoothOption (both with the company of the row). Order claims ('pending') are
   * never released here (they disappear when the BookedBooth exists or the order is cancelled).
   */
  const releaseLockRow = useCallback(async (row, booth) => {
    if (!row) throw new Error('This booth has no hold or option.');
    const input = {
      eventId,
      eventProductId: row.eventProductStockReservationsId || booth.eventProductId,
      companyId: row.companyStockReservationsId || row.company?.id,
      boothNumber: row.boothNumber || booth.boothNumber,
    };
    if (!input.companyId) throw new Error('The company of the reservation is unknown.');
    let result;
    if (row.status === 'hold') result = await releaseBoothReservation(input);
    else if (row.status === 'active') result = await releaseBoothOption(input);
    else if (row.status === 'pending') {
      throw new Error('This booth is claimed by an order. Cancel the order to release it.');
    } else throw new Error(`Reservations with status ${row.status || '-'} cannot be released here.`);
    assertActionResult(result, 'The reservation could not be released.');
    return result;
  }, [eventId]);

  /**
   * Reservations of a booth's EventProduct, newest first. With boothNumber (variant products) only the rows of
   * that booth; single-booth products list every row of the EventProduct (also legacy rows without number).
   */
  const listBoothReservations = useCallback(async (eventProductId, { boothNumber = null } = {}) => {
    const response = await client.graphql({ query: EVENT_PRODUCT_RESERVATIONS, variables: { id: eventProductId } });
    const key = normalizeBoothNumber(boothNumber);
    const items = (response.data.getEventProduct?.stockReservations?.items || [])
      .filter(Boolean)
      .filter((r) => !key || normalizeBoothNumber(r.boothNumber) === key);
    return items.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  }, []);

  /**
   * Releases the hold or option of one booth. The lock row (boothlock-<eventId>#<NUMBER>) is read first
   * (company + status) and released through the Lambda. Without a lock row, the admin rows with this booth
   * number (uuid ids, e.g. created in /admin/stockreservations) are deleted directly; order claims stay.
   */
  const releaseBoothLock = useCallback(async (booth) => {
    const response = await client.graphql({
      query: GET_STOCK_RESERVATION,
      variables: { id: boothLockId(eventId, booth.boothNumber) },
    });
    const lockRow = response.data.getStockReservation || null;
    let result;
    if (lockRow) {
      result = await releaseLockRow(lockRow, booth);
    } else {
      const rows = (await listBoothReservations(booth.eventProductId, { boothNumber: booth.boothNumber }))
        .filter((r) => cleanBoothNumber(r.boothNumber) && !isLockRowId(r) && isReleasableAdminRow(r));
      if (!rows.length) throw new Error('This booth has no hold or option.');
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await client.graphql({ query: DELETE_STOCK_RESERVATION, variables: { input: { id: row.id } } });
      }
      result = { success: true, deleted: rows.length };
    }
    await loadMap({ silent: true });
    return result;
  }, [eventId, listBoothReservations, loadMap, releaseLockRow]);

  /**
   * Removes a reservation: lock rows (id boothlock-<eventId>#<NUMBER>, holds / options) through the Lambda
   * fields; every other row (legacy rows without booth number, uuid admin rows with a booth number) with
   * deleteStockReservation; order claims are refused.
   */
  const deleteReservation = useCallback(async (reservation, booth = {}) => {
    if (reservation.status === 'pending' && reservation.orderId) {
      throw new Error('This booth is claimed by an order. Cancel the order to release it.');
    }
    if (isLockRowId(reservation) && (reservation.status === 'hold' || reservation.status === 'active')) {
      await releaseLockRow(reservation, booth);
    } else {
      await client.graphql({ query: DELETE_STOCK_RESERVATION, variables: { input: { id: reservation.id } } });
    }
    await loadMap({ silent: true });
  }, [loadMap, releaseLockRow]);

  // ----- Create booth products (single booths and variants) ------------------

  /**
   * Creates one booth Product + EventProduct per row, sequentially. ONE loop for both kinds of rows:
   * - single booth: { key, boothNumber, area, width, depth, linkProductId?, createNew? } (stock 1);
   * - variant: { key, variant, aliases?, area (m² of one booth), count, width, depth, linkProductId? }:
   *   one product for all `count` booths of the code (Product.boothVariant, boothVariantAliases e.g. ['9'] when
   *   the admin changed the SVG code '9' to 'Z9'), EventProduct stock = count (informational only);
   * - rule offer ("Create offer from features", boothOfferAdmin.featureRuleRows): { key, rule (match rule object,
   *   validated with boothMap.validateBoothMatchRule), label, nameCode ({{variant}} of the name), skuCode, area, count }:
   *   one product WITHOUT booth number and offer code whose Product.boothMatchRule is the row's rule (wins over a
   *   rule of the template); never linked to an existing product; EventProduct stock = count (informational).
   * Template product per row (row.templateId; undefined = the form-wide `templateId`): donates image, includes,
   * description, invoice name, buyer groups, category, booth height, price model (boothPriceMode,
   * boothPriceRule, boothMatchRule), its ProductComponents (included package), its industry links, its price
   * (when the row has none, boothRowPrice) and COPIES of its dimension PDF / GLB (copyBoothAsset into
   * public/booth-assets/<new product id>/; never a shared file).
   * Row price: row.price (boothRowPrice with priceMode 'fixed' | 'perSqm'); rows without a `price` key use
   * the form-wide pricePerSqm / fixedPrice (computeBoothPrice) of older callers.
   *
   * Safe to retry:
   * - The booth product list is re-read first. A single-booth row whose number already has a booth product
   *   (e.g. last year's, or an orphan of an earlier failed run) is linked to that product
   *   (`linkProductId`, only the EventProduct is created) or skipped with a hint - a second product
   *   with the same boothNumber/SKU is only created when the row explicitly says `createNew`.
   * - Variant rows: a product with the same code (or alias) in THIS event is refused (duplicate per event);
   *   variant products of other events are linked only on request (`linkProductId`), otherwise a new
   *   product is created (SKU with collision suffix).
   * - When a step after createProduct fails, the product and its already created children are
   *   deleted again, so the area stays unassigned and a retry starts from scratch.
   * Prices come from computeBoothPrice (same function as the modal preview, variant rows: m² of one booth);
   * never 0 EUR.
   *
   * Rows may also come from the stands / variants of a hall plan report (also of a draft version with errors).
   * Product names (de + en) come from `namePattern` (buildBoothProductNames: {{number}}, {{variant}}, {{area}},
   * {{size}}, {{count}}, {{template}}; empty = per-row default, or the template's name of a row with a template);
   * the booth number / variant code is stored separately.
   * Template copies follow the row (E2E review 2, N3 / N4; utils/boothOfferAdmin): the template's code and m² in
   * description, included package, invoice name and - without a name pattern - the name are replaced by the
   * row's (retargetTemplateText); when the m² differ, component quantities equal to the template's m² are scaled
   * to the row's m² and the offer is listed in `warnings` ({ label, from, to, scaled }).
   * Placeholder areas of unnamed shapes ('#<n>') are never turned into booths (skipped with a hint).
   *
   * @returns {Promise<{created: number, linked: number, createdBooths: number, linkedBooths: number, errors: string[],
   *   warnings: Array<{label: string, from: number, to: number, scaled: number}>, boothMap: object|null}>} created / linked = offers (products), *Booths = the booths they cover;
   *   boothMap = the booth map reloaded after the run
   */
  const createBoothProducts = useCallback(async ({
    rows, templateId = null, type, priceMode = 'fixed', pricePerSqm = null, fixedPrice = null, namePattern = '', onProgress,
  }) => {
    const errorsOut = [];
    // created offers whose template has another m² ({ label, from, to, scaled }); the form shows them translated
    const warningsOut = [];
    let created = 0;
    let linked = 0;
    let createdBooths = 0;
    let linkedBooths = 0;

    // template per row (row.templateId; undefined = the form-wide templateId, '' / null = none)
    const rowTemplateId = (row) => (row && row.templateId !== undefined ? row.templateId || null : templateId || null);
    const templateIds = [...new Set((rows || []).map(rowTemplateId).filter(Boolean))];
    const templates = new Map();
    const [existingProducts] = await Promise.all([
      // fresh list (throws on failure: never create blindly)
      Promise.all(BOOTH_PRODUCT_TYPES.map((productType) => queryAllPages(
        PRODUCTS_BY_TYPE,
        { type: productType },
        'productsByType',
        { allowPartial: true },
      ))).then((lists) => lists.flat()),
      Promise.all(templateIds.map((id) => Promise.all([
        client.graphql({ query: GET_TEMPLATE_PRODUCT, variables: { id } }),
        queryAllPages(LIST_PRODUCT_INDUSTRIES, { filter: { productId: { eq: id } } }, 'listAvailableProductsInIndustries'),
      ]).then(([productResponse, industries]) => {
        const product = productResponse.data.getProduct || null;
        if (!product) throw new Error(`Template product ${id} not found.`);
        templates.set(id, {
          product,
          industryIds: [...new Set(industries.map((i) => i.industryId).filter(Boolean))],
          components: (product.components?.items || []).filter(Boolean),
        });
      }))),
    ]);
    const offset = (boothMap?.booths?.length || 0) + 1;
    // booth products already in this event: from the map and from the current EventProduct list
    // (the map may lag behind a fresh change)
    const productIdsInEvent = new Set([
      ...(boothMap?.booths || []).map((b) => b.productId),
      ...(eventBoothProductsRef.current || []).map((ep) => ep.product?.id).filter(Boolean),
    ]);
    // variant codes (boothVariant + aliases) of this event's products -> label (per-event duplicate check)
    const eventCodes = new Map();
    const addEventCodes = (product) => variantCodesOf(product).forEach((code) => {
      if (!eventCodes.has(code)) eventCodes.set(code, boothProductLabel(product));
    });
    (eventBoothProductsRef.current || []).forEach((ep) => addEventCodes(ep.product));
    existingProducts.filter((p) => productIdsInEvent.has(p.id)).forEach(addEventCodes);
    const seenKeys = new Set();
    const usedSkus = new Set(); // SKUs created in this run (the sku index is eventually consistent)

    // boothSkuBase(code), or with '-2', '-3', ... when that SKU is already taken
    const nextFreeSku = async (code) => {
      const base = boothSkuBase(code);
      for (let suffix = 1; suffix < 100; suffix += 1) {
        const candidate = suffix === 1 ? base : `${base}-${suffix}`;
        if (!usedSkus.has(candidate)) {
          const response = await client.graphql({ query: PRODUCTS_BY_SKU, variables: { sku: candidate, limit: 1 } });
          if (!(response.data.productsBySku?.items || []).some(Boolean)) return candidate;
        }
      }
      throw new Error(`No free SKU for ${base}`);
    };

    const createEventProduct = (productId, price, index, stock) => client.graphql({
      query: CREATE_EVENT_PRODUCT,
      variables: {
        input: {
          eventEventProductsId: eventId,
          productEventProductsId: productId,
          price,
          stock,
          isAvailable: true,
          sortKey: offset + index,
        },
      },
    });

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const variant = cleanVariantCode(row.variant);
      const isVariantRow = Boolean(variant);
      // offer with a match rule instead of an offer code ("Create offer from features")
      const isRuleRow = !isVariantRow && Boolean(row.rule);
      const ruleCheck = isRuleRow ? validateBoothMatchRule(row.rule) : null;
      const n = isVariantRow || isRuleRow ? '' : String(row.boothNumber || '').trim();
      const label = isVariantRow ? variant : (isRuleRow ? String(row.label || row.skuCode || `rule ${i + 1}`) : n);
      if (typeof onProgress === 'function') onProgress(i, rows.length, label);

      let aliases = null;
      let existing = [];
      if (isRuleRow) {
        if (!ruleCheck.ok || !ruleCheck.rule) {
          errorsOut.push(`${label}: invalid match rule (${(ruleCheck.errors || []).join('; ') || 'empty'}) - skipped`);
          continue;
        }
        const ruleKey = `r:${JSON.stringify(ruleCheck.rule).toLowerCase()}`;
        if (seenKeys.has(ruleKey)) {
          errorsOut.push(`${label}: duplicate match rule in this run - skipped`);
          continue;
        }
        seenKeys.add(ruleKey);
        if (row.linkProductId) {
          errorsOut.push(`${label}: rule offers are always created as new products - skipped`);
          continue;
        }
      } else if (isVariantRow) {
        aliases = cleanVariantAliases(row.aliases, variant);
        const invalid = [variant, ...(aliases || [])].find((code) => !isValidVariantCode(code));
        if (invalid) {
          errorsOut.push(`${variant}: "${invalid}" is not a valid offer code - skipped`);
          continue;
        }
        const codes = [variant, ...(aliases || [])].map(normalizeVariant);
        if (codes.some((code) => seenKeys.has(`v:${code}`))) {
          errorsOut.push(`${variant}: duplicate offer code in this run - skipped`);
          continue;
        }
        codes.forEach((code) => seenKeys.add(`v:${code}`));
        const usedCode = codes.find((code) => eventCodes.has(code));
        if (usedCode) {
          errorsOut.push(`${variant}: offer code ${usedCode} is already used in this event by ${eventCodes.get(usedCode)} - skipped`);
          continue;
        }
        existing = findBoothProductsByVariant(existingProducts, codes);
      } else {
        const key = normalizeBoothNumber(n);
        if (!key) {
          errorsOut.push(`${i + 1}: no booth number - skipped`);
          continue;
        }
        if (isUnnamedBoothNumber(n)) {
          errorsOut.push(`${n}: shape without a booth number (not bookable) - skipped`);
          continue;
        }
        if (seenKeys.has(`n:${key}`)) {
          errorsOut.push(`${n}: duplicate booth number in this run - skipped`);
          continue;
        }
        seenKeys.add(`n:${key}`);
        existing = findBoothProductsByNumber(existingProducts, n);
        const inEvent = existing.find((p) => productIdsInEvent.has(p.id));
        if (inEvent) {
          errorsOut.push(`${n}: booth product ${inEvent.sku || inEvent.id} is already in this event - skipped`);
          continue;
        }
      }
      const area = numOrNull(row.area);
      const count = isVariantRow || isRuleRow ? Math.max(0, Math.round(numOrNull(row.count) ?? 0)) : 1;
      // EventProduct.stock: 1 per single booth; the booth count of an offer (informational only)
      const stock = count;
      const templateEntry = templates.get(rowTemplateId(row)) || null;
      const template = templateEntry?.product || null;
      // row price (per-row form); rows without a price key: the form-wide price fields of older callers
      const price = 'price' in row
        ? boothRowPrice({
          price: row.price, area, priceMode, template,
        })
        : (computeBoothPrice({
          area, priceMode, pricePerSqm, fixedPrice,
        }) ?? boothRowPrice({
          price: null, area, priceMode, template,
        }));

      // ----- link an existing booth product (only the EventProduct is created) -----
      if (row.linkProductId) {
        const product = existing.find((p) => p.id === row.linkProductId);
        if (!product) {
          errorsOut.push(isVariantRow
            ? `${label}: the product to link was not found or has another offer code - skipped`
            : `${label}: the product to link was not found or has another booth number - skipped`);
          continue;
        }
        if (productIdsInEvent.has(product.id)) {
          errorsOut.push(`${label}: product ${product.sku || product.id} is already in this event - skipped`);
          continue;
        }
        const productPrice = numOrNull(product.price);
        const linkPrice = price !== null ? price : (productPrice !== null && productPrice > 0 ? productPrice : null);
        if (linkPrice === null) {
          errorsOut.push(`${label}: no price (no m², no fixed price, no product price) - skipped`);
          continue;
        }
        try {
          await createEventProduct(product.id, linkPrice, i, stock);
          productIdsInEvent.add(product.id);
          addEventCodes(product);
          linked += 1;
          linkedBooths += count;
        } catch (error) {
          captureError(error, {
            feature: FEATURE,
            operation: 'link-booth-product-from-area',
            consoleMessage: 'Error linking an existing booth product to the event',
            context: {
              eventId, boothNumber: n || null, variant, productId: product.id,
            },
          });
          errorsOut.push(`${label}: ${errorMessage(error)}`);
        }
        continue;
      }

      if (!isVariantRow && !isRuleRow && existing.length > 0 && !row.createNew) {
        errorsOut.push(`${n}: a booth product with this number already exists (${existing.map((p) => p.sku || p.id).join(', ')}) - link it instead; skipped`);
        continue;
      }
      if (price === null) {
        errorsOut.push(`${label}: no price (enter a price for the row or choose a template with a price) - skipped`);
        continue;
      }

      // ----- create a new booth product (rolled back on failure) -----
      // rule offers: SKU from the features ('CP-ST-12cornerV'), the name gets the position / zone keys as {{variant}}
      const skuCode = isRuleRow ? String(row.skuCode || label) : label;
      let sku = boothSkuBase(skuCode);
      let productId = null;
      const componentIds = [];
      const industryLinkIds = [];
      // files copied from the template into the folder of the new product (removed again on failure)
      const copiedPaths = [];
      try {
        sku = await nextFreeSku(skuCode);
        usedSkus.add(sku);
        // texts copied from the template name the template's code and m²: they follow the row (review 2, N3)
        const retarget = templateRetargetValues(template, { variant: isVariantRow ? variant : '', boothNumber: n, area });
        const defaultNames = buildBoothProductNames(namePattern, {
          boothNumber: n,
          variant: isRuleRow ? String(row.nameCode ?? '') : variant || '',
          area,
          count,
          templateName: germanText(template?.name),
        });
        // without a name pattern the template's own name is taken over (retargeted); a pattern always wins.
        // Rule offers have no code to put in place of the template's: they keep the default name.
        const names = template && !isRuleRow && !String(namePattern || '').trim()
          ? retargetTemplateNames(template.name, retarget, defaultNames)
          : defaultNames;
        // the id is chosen here, so the template's PDF / GLB can be copied into public/booth-assets/<id>/
        // before the product exists: products never share a stored file (the booth editor deletes replaced files)
        const newId = newProductId();
        const copiedFiles = {};
        for (const field of ['boothDimensionFile', 'booth3DModel']) {
          const source = template?.[field];
          if (source && source.s3Path) {
            const copied = await copyBoothAsset(source.s3Path, newId, { fileName: source.fileName, type: source.type });
            copiedPaths.push(copied.s3Path);
            copiedFiles[field] = fileInputOrNull(copied);
          }
        }
        const input = {
          id: newId,
          name: [
            { language: 'de', text: names.de },
            { language: 'en', text: names.en },
          ],
          sku,
          type,
          price,
          stock,
          visibleInShop: false,
          allowedBuyerGroups: template?.allowedBuyerGroups?.length ? template.allowedBuyerGroups : ['business'],
          icon: 'store',
          // rule "number XOR variant"; a rule offer has neither (its match rule is set below)
          ...(isVariantRow ? { boothVariant: variant, boothVariantAliases: aliases } : {}),
          ...(!isVariantRow && !isRuleRow ? { boothNumber: n } : {}),
          boothArea: area,
          boothWidth: numOrNull(row.width),
          boothDepth: numOrNull(row.depth),
        };
        if (template) {
          const image = multiImageInput(template.image);
          if (image) input.image = image;
          const includes = multiLangInput(retargetMultiLang(template.includes, retarget));
          if (includes) input.includes = includes;
          const description = multiLangInput(retargetMultiLang(template.description, retarget));
          if (description) input.description = description;
          const invoiceName = multiLangInput(retargetMultiLang(template.invoiceName, retarget));
          if (invoiceName && invoiceName.some((entry) => String(entry.text || '').trim())) input.invoiceName = invoiceName;
          if (template.productCategoryProductsId) input.productCategoryProductsId = template.productCategoryProductsId;
          if (numOrNull(template.boothHeight) !== null) input.boothHeight = template.boothHeight;
          // price model of the offer (booth offering model): price mode, price rule, match rule
          if (String(template.boothPriceMode || '').trim()) input.boothPriceMode = template.boothPriceMode;
          const priceRule = awsJsonInput(template.boothPriceRule);
          if (priceRule) input.boothPriceRule = priceRule;
          const matchRule = awsJsonInput(template.boothMatchRule);
          if (matchRule) input.boothMatchRule = matchRule;
          Object.assign(input, copiedFiles);
        }
        // the row's own rule wins over a rule copied from the template
        if (isRuleRow) input.boothMatchRule = JSON.stringify(row.rule);
        const productResponse = await client.graphql({ query: CREATE_PRODUCT, variables: { input } });
        productId = productResponse.data.createProduct.id;

        // quantities equal to the template's m² (carpet per m²) follow the row's m² (review 2, N4)
        const scaledComponents = scaleTemplateComponents(templateEntry?.components || [], { fromSize: retarget.fromSize, toSize: area });
        for (const component of scaledComponents.components) {
          const componentResponse = await client.graphql({
            query: CREATE_PRODUCT_COMPONENT,
            variables: {
              input: {
                parentProductID: productId,
                componentProductID: component.componentProductID,
                qty: component.qty || 1,
                order: component.order ?? null,
              },
            },
          });
          const componentId = componentResponse.data.createProductComponent?.id;
          if (componentId) componentIds.push(componentId);
        }
        for (const industryId of templateEntry?.industryIds || []) {
          const industryResponse = await client.graphql({ query: CREATE_PRODUCT_INDUSTRY, variables: { input: { productId, industryId } } });
          const industryLinkId = industryResponse.data.createAvailableProductsInIndustries?.id;
          if (industryLinkId) industryLinkIds.push(industryLinkId);
        }
        await createEventProduct(productId, price, i, stock);
        productIdsInEvent.add(productId);
        if (isVariantRow) addEventCodes({ id: productId, sku, boothVariant: variant, boothVariantAliases: aliases });
        created += 1;
        createdBooths += count;
        if (scaledComponents.sizeChange) {
          warningsOut.push({
            label,
            from: scaledComponents.sizeChange.from,
            to: scaledComponents.sizeChange.to,
            scaled: scaledComponents.scaled.length,
          });
        }
      } catch (error) {
        captureError(error, {
          feature: FEATURE,
          operation: isVariantRow ? 'create-variant-booth-product' : 'create-booth-from-area',
          consoleMessage: isVariantRow ? 'Error creating an offer (booth product with offer code)' : 'Error creating a booth from a hall plan area',
          context: {
            eventId, boothNumber: n || null, variant, templateId: rowTemplateId(row), productId,
          },
        });
        // copied template files: nothing references them unless the product still exists (see below)
        const removeCopies = () => Promise.all(copiedPaths.map((s3Path) => removeBoothAsset(s3Path).catch((removeError) => {
          captureError(removeError, {
            feature: FEATURE,
            operation: 'rollback-copied-booth-file',
            consoleMessage: 'Error removing a copied template file of a half-created booth product',
            context: { eventId, productId, s3Path },
          });
        })));
        if (!productId) await removeCopies();
        let rollbackNote = '';
        if (productId) {
          // the EventProduct is the last step, so it does not exist here: undo product + children
          const rollback = await Promise.allSettled([
            ...industryLinkIds.map((id) => client.graphql({ query: DELETE_PRODUCT_INDUSTRY, variables: { input: { id } } })),
            ...componentIds.map((id) => client.graphql({ query: DELETE_PRODUCT_COMPONENT, variables: { input: { id } } })),
          ]);
          let productDeleted = false;
          try {
            await client.graphql({ query: DELETE_PRODUCT, variables: { input: { id: productId } } });
            productDeleted = true;
          } catch (rollbackError) {
            captureError(rollbackError, {
              feature: FEATURE,
              operation: 'rollback-booth-from-area',
              consoleMessage: 'Error deleting a half-created booth product',
              context: {
                eventId, boothNumber: n || null, variant, productId,
              },
            });
          }
          const childFailures = rollback.filter((r) => r.status === 'rejected');
          if (childFailures.length > 0) {
            captureError(childFailures[0].reason, {
              feature: FEATURE,
              operation: 'rollback-booth-children-from-area',
              consoleMessage: 'Error deleting components / industry links of a half-created booth product',
              context: {
                eventId, boothNumber: n || null, variant, productId, failures: childFailures.length,
              },
            });
          }
          if (!productDeleted) {
            rollbackNote = ` (cleanup failed: product ${sku} remains without event link - it is offered for linking on retry, check its included package)`;
          } else {
            // the product is gone: its copied files are orphans now
            await removeCopies();
          }
        }
        errorsOut.push(`${label}: ${errorMessage(error)}${rollbackNote}`);
      }
    }
    if (typeof onProgress === 'function') onProgress(rows.length, rows.length, '');
    boothProductsRef.current = null; // new products: reload the product list next time
    setBoothProducts(null);
    // the refreshed map lets callers re-match a hall plan report (null when it could not be loaded)
    const refreshedMap = await reloadBooths();
    return {
      created, linked, createdBooths, linkedBooths, errors: errorsOut, warnings: warningsOut, boothMap: refreshedMap,
    };
  }, [boothMap, eventId, reloadBooths]);

  return {
    event,
    planImageUrl,
    areas,
    activeVersionId,
    versions,
    areaMeta,
    boothMap,
    boothProducts,
    eventBoothProducts,
    loading,
    errors,
    loadEvent,
    loadAreas,
    loadMap,
    loadEventBoothProducts,
    reloadBooths,
    reloadBoothsDelayed,
    refreshAll,
    readPlanFileDimensions,
    uploadPlanImage,
    removePlanImage,
    savePlanDimensions,
    importAreas,
    createArea,
    deleteArea,
    updateAreaVariant,
    updateAreaFeatures,
    updateAreaBookable,
    writeAreaVariants,
    buildAreasExport,
    loadVersions,
    loadVersionAreas,
    analyzeHallPlanFile,
    saveHallPlanVersion,
    previewCarryOver,
    activateVersion,
    deactivateVersion,
    deleteVersion,
    previewVersionVariants,
    applyVersionVariants,
    updateBoothEventProduct,
    removeBoothFromEvent,
    updateProductBoothNumber,
    loadBoothProducts,
    addBoothProductsToEvent,
    searchCompanies,
    createOptionForCompany,
    releaseBoothLock,
    listBoothReservations,
    deleteReservation,
    createBoothProducts,
  };
}

export default useEventBoothPlan;
